import {
  Box,
  Divider,
  Fade,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  HStack,
  MenuItem,
  Modal,
  ModalOverlay,
  Spacer,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  faChain,
  faChainSlash,
  faEyeSlash,
  faPencil,
} from '@fortawesome/free-solid-svg-icons';
import {
  ColumnOrderState,
  ColumnSizingState,
  ColumnSort,
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingFn,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { useFlag } from '@unleash/proxy-client-react';
import {
  flatMap,
  fromPairs,
  isEmpty,
  isEqual,
  isNil,
  map,
  omit,
  partition,
  pick,
  round,
  uniq,
} from 'lodash';
import {
  DragEvent,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useMutation, useQuery } from 'urql';
import { onEnd, pipe, subscribe, Subscription } from 'wonka';
import { ReactComponent as SigmaIcon } from '../../../assets/icons/sigma-icon.svg';

import { calculateGeometry$ } from '../../../domain/geometric-operations';
import {
  GeometricProperty,
  OperationResult,
  Point,
  SPARKEL_GEOMETRIC_PROPERTY_SET,
  SPARKEL_LOCATION_PROPERTY_SET,
} from '../../../domain/geometry/geometric-types';
import { ResolvedQuantity } from '../../../domain/property-types';
import {
  ResolvedCustomField,
  ResolvedOrderEntry,
} from '../../../domain/resolve-order-entry';
import {
  ColumnType,
  CreateOrderEntryDocument,
  CreateOrderEntryFromTemplateDocument,
  CreateOrderEntryFromTemplateInput,
  CreateOrderEntryInput,
  CreateTemplateFromOrderEntryDocument,
  CurrencyEnum,
  CustomFieldInput,
  CustomFieldsOrderEntryIdFkeyCustomFieldsCreateInput,
  ElementIdentifierDbidTypeInput,
  EventResource,
  EventType,
  FlattenSubrowsDocument,
  LengthUnitTypeEnum,
  ForgeAttributeQueryTypeInput,
  ForgeAttributeType,
  GetProjectDeepDocument,
  GetProjectSettingsDocument,
  MoveOrCopyOrderEntriesDocument,
  MoveOrCopyOrderEntriesInput,
  OrderColumnDeepFragment,
  OrderColumnPatch,
  OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate,
  OrderEntryPatch,
  PropertySetType,
  QuantityEnum,
  QuantityInput,
  QuantityOperation,
  QuantityType,
  QuantityTypeInput,
  Unit,
  UpdateOrderDocument,
  UpdateOrderEntryInput,
  UpdateOrderInput,
} from '../../../gql/graphql';
import { useLocalStorage } from '../../../hooks/local-storage';
import { usePropertyContext } from '../../../hooks/property-resolving';
import { useSheetShapes } from '../../../hooks/sheet-shapes';
import { useLegendEntriesContext } from '../../viewers/SheetsViewerV2/hooks/LegendEntriesContext';
import {
  SuggestionInfo,
  useSuggestedResources,
} from '../../../hooks/suggested-resources';
import { useUserTenant } from '../../../services/auth-info';
import {
  EventName,
  EventProperties,
  useMixpanel,
} from '../../../services/mixpanel';
import {
  hasDbIds,
  mergeRecordOfLists,
  removeElementsFromRecords,
} from '../../../services/viewer-services';
import { useColorization } from '../../../services/viewer/colorization';
import { useForgeContextMenuContext } from '../../../services/viewer/ForgeContextMenu';
import { OnDemandGeometryVisualization } from '../../../services/viewer/result-visualisation/OnDemandGeometryVisualization';
import { useSelection } from '../../../services/viewer/selection';
import { useSheetContextMenuContext } from '../../../services/viewer/SheetsContextMenu';
import { useVisibility } from '../../../services/viewer/visibility';
import { exportTableToExcel } from '../../../utils/excel-utils';
import {
  ColumnNameAndId,
  parseDerivedFormula,
  stringifyDerivedFormula,
} from '../../../utils/formula-utils';
import { findNextAvailableNameForCopy } from '../../../utils/naming-utils';
import {
  parseDragData,
  PROPERTY_DRAG_DATA_FORMAT,
} from '../../../utils/property-dragging-utils';
import { formatQuantity } from '../../../utils/quantity-utils';
import { COLORS, colorsByLeastUsed } from '../../../utils/row-color-util';
import { tierLimits } from '../../../utils/tier-utils';
import {
  UnitTypeToUnit,
  UnitTypeToUnitSymbol,
} from '../../../utils/unit-utils';
import { DeleteMenuItem } from '../../common/DeleteMenuItem';
import { DbIdsPerModel, useViewer } from '../../common/ForgeViewer';
import SparkelIcon from '../../common/icon/SparkelIcon';
import {
  Panel,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../../common/Panel';
import { usePanelFullscreen } from '../../common/PanelFullscreenContext';
import { useSheetViewer } from '../../common/SheetViewer';
import { useViewerMode, ViewerMode } from '../../common/viewer-mode';
import { useViewManager } from '../../common/ViewManagerContext';
import { RowSelectCell, RowSelectHeader } from '../../table/RowSelect';
import SparkelTable, {
  EditableTableMeta,
  PriceCell,
  SparkelTableColumnMeta,
  UnitCell,
  UnitPriceCell,
} from '../../table/Table';
import { getColumnName } from '../../table/table-utils';
import PlaceholderText from '../../text/PlaceholderText';
import { getSheetShapeUrn } from '../../viewers/common/hooks/useSheetShapesManager';
import { getTranslatedDefaultColumnName } from '../aggregated-order-panel/utils';
import { Buy } from '../project-modal/Buy';
import { useProjectHistory } from '../project-history/hooks/useProjectHistory';
import { HISTORY_COLORS } from '../project-history/hooks/useDiffing';
import AddCustomColumnHeader, {
  AddCustomColumnMeta,
} from './AddCustomColumnHeader';
import CustomColumnCell, { CustomColumnMeta } from './CustomColumnCell';
import DynamicQuantitiesModal from './DynamicQuantitiesModal';
import DynamicQuantityCell, {
  DynamicQuantitiesColumnMeta,
} from './DynamicQuantityCell';
import { ExpanderCell } from './ExpanderCell';
import { LinkedCountCell, LinkedCountMeta } from './LinkedCountCell';
import { OrderEntriesPanelToolbar } from './OrderEntriesPanelToolbar';
import SuggestedResourceList from './SuggestedResourceList';
import TitleCell from './TitleCell';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import { useEnabledSheetViewer } from 'src/components/viewers/common/hooks/useEnabledSheetViewer';
// Types used only for runtime type-narrowing (via 'in' checks)
import type { SheetViewerContextType } from 'src/components/common/SheetViewer';
import type { SheetsViewerV2ContextType } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.context';
import { ColorPicker } from 'src/components/table/ColorPicker';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { useEventHistory } from 'src/hooks/event-history';

export type TableEntry = Partial<ResolvedOrderEntry> &
  Required<Pick<ResolvedOrderEntry, 'id' | 'name'>>;

export type EditableRowFormValues = {
  name: string | null;
  unit?: Unit | null;
  color?: string | null;
  unitPrice?: number | null;
  currency?: CurrencyEnum | null;
  quantity?: QuantityTypeInput;
  custom?: Record<string, { id: string; quantity?: QuantityTypeInput }>;
};

export type EditableRowsFormValues = {
  [rowId: string]: EditableRowFormValues;
};
type OrderEntriesPanelProps = {
  title: string;
  data: TableEntry[];
  orderColumns: OrderColumnDeepFragment[];
  propertySets: PropertySetType[];
  updateEntries: (
    rowData: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate[]
  ) => Promise<void>;
  deleteEntries: (entryIds: string[]) => Promise<void>;
  addCustomColumn: (columnName: string) => Promise<string>;
  saveColumns: (orderColumns: OrderColumnPatch[]) => Promise<void>;
  deleteCustomColumn: (columnId: string) => Promise<void>;
  hideColumn: (columnId: string) => Promise<void>;
  updateShowColumnTotal: (columnId: string, value: boolean) => Promise<void>;
  readOnly: boolean;
  updating: boolean;
};

export type NewRowFormValues = Omit<
  EditableRowFormValues,
  'createdAt' | 'updatedAt' | 'id' | 'subRows'
>;

const compareLinkedCount: SortingFn<TableEntry> = (row1, row2, columnId) => {
  const value1 = row1.getValue(columnId) as { count: number };
  const value2 = row2.getValue(columnId) as { count: number };
  return value1.count > value2.count ? 1 : -1;
};

const selectColumnId = 'select';
const expanderColumnId = 'expander';
const addColumnId = 'addColumn';

const columnHelper = createColumnHelper<TableEntry>();

export function getEntryDiffPath(rows: Row<TableEntry>[], rowId: string) {
  let subPath = '';
  const keySpace = rowId.split('.');
  for (let i = 2; i <= keySpace.length; i++) {
    const cKey = keySpace.slice(0, i).join('.');
    const found = rows.find((cRow) => cRow.id === cKey);
    if (found) {
      // we should also have a collection diff for every level
      const subKey = `.subRows.${found.original.name}`;
      subPath += subKey;
    }
  }
  return subPath;
}

// eslint-disable-next-line complexity
export function OrderEntriesPanel({
  title,
  data,
  updateEntries,
  deleteEntries,
  addCustomColumn,
  saveColumns,
  deleteCustomColumn,
  hideColumn,
  updateShowColumnTotal,
  propertySets,
  orderColumns,
  readOnly,
  updating,
}: OrderEntriesPanelProps): ReactElement {
  const { orderId, projectId } = useParams<{
    orderId: string;
    projectId: string;
  }>() as { orderId: string; projectId: string };
  const { isFullscreen } = usePanelFullscreen();

  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: {
      id: projectId,
    },
  });
  const [{ data: projectSettingsData }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: {
      projectId: projectId,
    },
  });
  const [sorting, setSorting] = useLocalStorage<ColumnSort[]>(
    `${orderId}-order-table-sorting`,
    []
  );
  const toast = useToast();

  const { trackEvent } = useMixpanel();

  const [editingRows, setEditingRows] = useState<string[]>([]);
  const locale = navigator.language || 'nb-NO';

  const { propertyContext } = usePropertyContext(projectId);

  const { selectedDbIds, selectDbIds, setSelectedDbIds } = useSelection();

  // Get sheet shapes to filter out legends from linking
  const { shapes: allSheetShapes } = useSheetShapes();

  // Filter out legend shapes from selectedDbIds for linking operations
  const selectedDbIdsWithoutLegends = useMemo(() => {
    if (!allSheetShapes.data) return selectedDbIds;

    // Build a set of legend dbIds per urn
    const legendDbIds: Record<string, Set<number>> = {};
    for (const shape of allSheetShapes.data) {
      if (shape.sheetShapeLegend) {
        if (!legendDbIds[shape.urn]) {
          legendDbIds[shape.urn] = new Set();
        }
        legendDbIds[shape.urn].add(shape.dbId);
      }
    }

    // Filter out legend dbIds from selectedDbIds
    const filtered: Record<string, number[]> = {};
    for (const [urn, dbIds] of Object.entries(selectedDbIds)) {
      const legendSet = legendDbIds[urn];
      if (legendSet) {
        const filteredDbIds = dbIds.filter((dbId) => !legendSet.has(dbId));
        if (filteredDbIds.length > 0) {
          filtered[urn] = filteredDbIds;
        }
      } else {
        filtered[urn] = dbIds;
      }
    }
    return filtered;
  }, [selectedDbIds, allSheetShapes.data]);

  //Use suggested resources.
  const { suggestedResources } = useSuggestedResources(projectId, orderId);

  const mixpanelProperties: EventProperties = useMemo(
    () => ({
      'Project Name': projectData?.project?.name,
      'Project ID': projectId,
      'Table Name': title,
      'Table ID': orderId,
    }),
    [orderId, projectData?.project?.name, projectId, title]
  );

  // Callbacks (may move to separate file/hook at some point)
  const getUpdatedDbidFields = (
    dbIds: DbIdsPerModel
  ): Pick<
    OrderEntryPatch,
    'elementIdentifiersDbid' | 'elementIdentifiersQuery'
  > => {
    return {
      elementIdentifiersDbid: dbIdsPerModelToInput(dbIds),
      elementIdentifiersQuery: null,
    };
  };

  const onSelectLinkedElements = useCallback(
    (event: React.MouseEvent, row: Row<TableEntry>) => {
      event.stopPropagation();
      const ids = fromPairs(
        row.original.resolvedElementIdentifiersDbid?.map(
          ({ modelUrn, dbIds }) => [modelUrn, dbIds]
        ) ?? []
      );
      if (event.shiftKey) {
        selectDbIds(ids);
      } else {
        setSelectedDbIds(ids);
      }
    },
    [selectDbIds, setSelectedDbIds]
  );

  const [activeQuantityCell, setActiveQuantityCell] = useState<{
    columnName: string;
    rowId: string;
    quantityType: GeometricProperty;
  } | null>(null);
  const [isDrawingQuantityColumn, setDrawingQuantityColumn] =
    useState<boolean>(false);

  const [visualizedResults, setVisualizedResults] = useState<OperationResult[]>(
    []
  );

  const visualizerSubscriptionTeardownRef =
    useRef() as MutableRefObject<Subscription | null>;

  useEffect(() => {
    return () => {
      visualizerSubscriptionTeardownRef.current?.unsubscribe();
    };
  }, []);

  const handleActiveQuantityCellClick = useCallback(
    async (columnName: string, selectedEntry: Row<TableEntry>) => {
      // NOTE: For now this _may_ allow the user to "clear" drawings without anything actually being drawn on the canvas
      // Not sure how to remedy that at this point
      if (
        !isDrawingQuantityColumn &&
        activeQuantityCell !== null &&
        activeQuantityCell.rowId === selectedEntry.id &&
        activeQuantityCell.columnName === columnName
      ) {
        setVisualizedResults([]);
        setDrawingQuantityColumn(false);
        setActiveQuantityCell(null);
        visualizerSubscriptionTeardownRef.current?.unsubscribe();
        return;
      }

      if (columnName)
        if (
          selectedEntry.original.resolvedElementIdentifiersDbid &&
          selectedEntry.original.resolvedElementIdentifiersDbid.length > 0
        ) {
          const quantity = getQuantityForVisualizedRow(
            columnName,
            selectedEntry,
            orderColumns
          );

          const algorithmType =
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            quantity?.calculation!.attribute!.name as GeometricProperty;

          setVisualizedResults([]);
          setDrawingQuantityColumn(true);
          setActiveQuantityCell({
            rowId: selectedEntry.id,
            columnName: columnName,
            quantityType: algorithmType,
          });

          const results: OperationResult[] = [];
          visualizerSubscriptionTeardownRef.current = pipe(
            calculateGeometry$(
              algorithmType,
              selectedEntry.original.resolvedElementIdentifiersDbid,
              propertyContext,
              cleanedProjectSettings
            ),
            onEnd(() => {
              setDrawingQuantityColumn(false);
              visualizerSubscriptionTeardownRef.current = null;
            }),
            subscribe((result) => {
              if (!result) {
                console.warn(
                  'Received an unexpected empty operation result for geometric property calculation'
                );
                return;
              }
              results.push(result);
              setVisualizedResults(results);
            })
          );
        }
    },
    [isDrawingQuantityColumn, activeQuantityCell, orderColumns, propertyContext]
  );

  const [currentMassAssignColumn, setCurrentMassAssignColumn] = useState<
    string | null
  >(null);

  const { t } = useTranslation(['table']);

  const ShowTotalMenuItem = useCallback(
    ({ column }: { column: OrderColumnDeepFragment }) => {
      return (
        <FormControl
          as={HStack}
          aria-label={`Show totals ${column.name}`}
          fontSize={'xs'}
          paddingX={3}
          paddingY={1.5}
          gap={3}
        >
          <SparkelIcon as={SigmaIcon} />
          <FormLabel
            htmlFor={column.id}
            fontSize={'xs'}
            margin={0}
            cursor={'pointer'}
          >
            {t('show-total')}
          </FormLabel>
          <Spacer />
          <Switch
            id={column.id}
            size="sm"
            isChecked={column.showTotal}
            colorScheme="brand"
            onChange={(e) => updateShowColumnTotal(column.id, e.target.checked)}
          />
        </FormControl>
      );
    },
    [t, updateShowColumnTotal]
  );

  const unitSettings = useMemo(() => {
    return projectSettingsData?.project?.settings?.unitSettings;
  }, [projectSettingsData?.project?.settings?.unitSettings]);

  const cleanedProjectSettings = useMemo(() => {
    const globalOffset =
      projectSettingsData?.project?.settings?.viewerGlobalOffset;
    return {
      maxTrianglesForComplexAlgorithm:
        projectSettingsData?.project?.settings?.polygonCalculationLimit ?? 5000,
      globalOffset: (globalOffset
        ? [globalOffset.x, globalOffset.y, globalOffset.z]
        : [0, 0, 0]) as Point,
      unitSettings:
        projectSettingsData?.project?.settings?.unitSettings ?? undefined,
      manuallyApplyFilters:
        projectSettingsData?.project?.settings?.manuallyApplyFilters ?? false,
    };
  }, [projectSettingsData?.project?.settings]);

  const { isHistoryDrawerOpen, diff } = useProjectHistory();

  const tableColumns =
    // eslint-disable-next-line complexity
    useMemo(() => {
      const nameColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.Description
      );
      const quantityColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.Quantity
      );
      const unitColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.Unit
      );
      const colorColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.Color
      );
      const unitPriceColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.UnitPrice
      );
      const linkedCountColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.LinkedCount
      );
      const totalColumn = orderColumns.find(
        (e) => e.columnType === ColumnType.Total
      );

      if (
        !nameColumn ||
        !quantityColumn ||
        !unitPriceColumn ||
        !colorColumn ||
        !unitColumn ||
        !linkedCountColumn ||
        !totalColumn
      ) {
        throw new Error('Default columns missing');
      }

      const cols = [
        columnHelper.display({
          id: selectColumnId,
          size: 40,
          minSize: 40,
          enableResizing: true,
          header: RowSelectHeader,
          cell: RowSelectCell,
        }),
        columnHelper.display({
          id: expanderColumnId,
          size: 24,
          minSize: 24,
          enableResizing: false,
          enableSorting: false,
          cell: ExpanderCell,
        }),
        columnHelper.accessor('name', {
          header: getTranslatedDefaultColumnName(nameColumn.name, t),
          id: columnTypeToId(ColumnType.Description),
          size: 200,
          minSize: 40,
          meta: {
            isRequired: true,
            menuItems: readOnly ? null : (
              <MenuItem
                aria-label={`Hide column ${nameColumn.name}`}
                icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                onClick={() => hideColumn(nameColumn.id)}
              >
                {t('hide-column')}
              </MenuItem>
            ),
          },
          cell: TitleCell,
        }),
        ...orderColumns
          .filter((e) => e.columnType === ColumnType.Custom)
          .map((column) =>
            columnHelper.accessor(
              ({ customFields }) => {
                const resolvedQuantity = customFields?.find(
                  (field) => field.orderColumnId === column.id
                )?.resolvedValue;
                if (resolvedQuantity) {
                  return formatQuantity(resolvedQuantity);
                }
              },
              {
                id: column.id,
                size: 100,
                minSize: 40,
                footer: column.showTotal
                  ? (props) => {
                      const sum = props.column
                        .getFacetedRowModel()
                        .rows.filter((row) => row.depth === 0)
                        .map((row) => {
                          const customField = row.original.customFields?.find(
                            (field) => field.orderColumnId === column.id
                          );
                          if (customField) {
                            if (
                              Number.isNaN(customField.resolvedValue.value) ||
                              (Array.isArray(customField.resolvedValue.value) &&
                                uniq(customField.resolvedValue.value).length >
                                  1)
                            ) {
                              return Number.NaN;
                            }
                            return Number.parseFloat(
                              customField.resolvedValue.value.toString()
                            );
                          }
                          return 0;
                        })
                        .reduce((acc, value) => acc + value, 0);

                      return round(sum, 2).toString();
                    }
                  : undefined,
                sortingFn: 'alphanumericCaseSensitive',
                meta: {
                  canDrop: true,
                  menuItems: readOnly
                    ? null
                    : (tableInstance: Table<TableEntry>) => (
                        <>
                          {tableInstance
                            .getSelectedRowModel()
                            .rows.filter((row) => row.depth === 0).length >
                          0 ? (
                            <>
                              <MenuItem
                                aria-label={`Mass assign ${column.name} definition`}
                                icon={
                                  <SparkelIcon icon={faPencil} fixedWidth />
                                }
                                onClick={() =>
                                  void setCurrentMassAssignColumn(column.id)
                                }
                              >
                                {t('set-column-dynamic-value')}
                              </MenuItem>
                              <DynamicQuantitiesModal
                                projectSettings={cleanedProjectSettings}
                                propertyContext={propertyContext}
                                propertySets={propertySets}
                                isOpen={column.id === currentMassAssignColumn}
                                onClose={() =>
                                  void setCurrentMassAssignColumn(null)
                                }
                                defaultCalculation={null}
                                dbIds={[]}
                                onLink={(
                                  calculation,
                                  _,
                                  unitFormatOverride
                                ) => {
                                  const updatedRows: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate[] =
                                    tableInstance
                                      .getSelectedRowModel()
                                      .rows.map((row) => {
                                        let input: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate;
                                        const customField =
                                          row.original.customFields?.find(
                                            (field) =>
                                              field.orderColumnId === column.id
                                          );
                                        if (customField) {
                                          input = {
                                            id: row.original.id,
                                            patch: {
                                              customFields: {
                                                updateByOrderColumnIdAndOrderEntryId:
                                                  [
                                                    {
                                                      orderColumnId: column.id,
                                                      orderEntryId:
                                                        row.original.id,
                                                      patch: {
                                                        value: {
                                                          type: QuantityEnum.Dynamic,
                                                          calculation:
                                                            calculation,
                                                          stringValue: null,
                                                          value: null,
                                                          derivedFormula: null,
                                                          unitFormatOverride,
                                                        },
                                                      },
                                                    },
                                                  ],
                                              },
                                            },
                                          };
                                        } else {
                                          input = {
                                            id: row.original.id,
                                            patch: {
                                              customFields: {
                                                create: [
                                                  {
                                                    orderColumnId: column.id,
                                                    value: {
                                                      type: QuantityEnum.Dynamic,
                                                      calculation: calculation,
                                                      stringValue: null,
                                                      value: null,
                                                      derivedFormula: null,
                                                      unitFormatOverride,
                                                    },
                                                  },
                                                ],
                                              },
                                            },
                                          };
                                        }
                                        return input;
                                      });
                                  updateEntries(updatedRows)
                                    .then(() => {
                                      // I've opted for "duplicating" the toast dispatching here, as the `updater` pattern is not accessible from this scope
                                      toast({
                                        status: 'success',
                                        title: t(
                                          'toast.success.update-column-definition'
                                        ),
                                        position: 'top',
                                        duration: 2500,
                                      });
                                    })
                                    .catch(() => {
                                      toast({
                                        status: 'error',
                                        title: t(
                                          'toast.error.update-column-definition'
                                        ),
                                        position: 'top',
                                      });
                                    });
                                }}
                                onUnlink={() => {
                                  const updatedRows: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate[] =
                                    [];
                                  tableInstance
                                    .getSelectedRowModel()
                                    .rows.forEach((row) => {
                                      const customField =
                                        row.original.customFields?.find(
                                          (customField) =>
                                            customField.orderColumnId ===
                                            column.id
                                        );
                                      if (customField) {
                                        const input: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate =
                                          {
                                            id: row.original.id,
                                            patch: {
                                              customFields: {
                                                deleteById: [
                                                  {
                                                    id: customField.id,
                                                  },
                                                ],
                                              },
                                            },
                                          };
                                        updatedRows.push(input);
                                      }
                                    });
                                  updateEntries(updatedRows)
                                    .then(() => {
                                      toast({
                                        status: 'success',
                                        title: t(
                                          'toast.success.clear-column-definition'
                                        ),
                                        position: 'top',
                                        duration: 2500,
                                      });
                                    })
                                    .catch(() => {
                                      toast({
                                        status: 'error',
                                        title: t(
                                          'toast.error.clear-column-definition'
                                        ),
                                        position: 'top',
                                      });
                                    });
                                }}
                              />
                            </>
                          ) : null}
                          <Divider />
                          <ShowTotalMenuItem column={column} />
                          <Divider />
                          <MenuItem
                            aria-label={`Hide column ${column.name}`}
                            icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                            onClick={() => hideColumn(column.id)}
                          >
                            {t('hide-column')}
                          </MenuItem>
                          <DeleteMenuItem
                            name={column.name}
                            onDelete={() =>
                              deleteCustomColumn(column.id).then(() =>
                                trackEvent(EventName.CustomColumnDeleted, {
                                  ...mixpanelProperties,
                                  'Column Name': column.name,
                                })
                              )
                            }
                          />
                        </>
                      ),
                  projectId: projectId,
                  propertySets: propertySets,
                  orderColumns: orderColumns,
                  columnName: column.name,
                  isDrawingQuantityColumn,
                  activeQuantityCell,
                  handleActiveQuantityCellClick,
                  projectSettings: cleanedProjectSettings,
                } as CustomColumnMeta & SparkelTableColumnMeta,
                header: column.name,
                cell: CustomColumnCell,
              }
            )
          ),
        columnHelper.accessor('resolvedQuantity', {
          header: getTranslatedDefaultColumnName(quantityColumn.name, t),
          id: columnTypeToId(ColumnType.Quantity),
          size: 200,
          footer: quantityColumn.showTotal
            ? (props) => {
                const sum = props.column
                  .getFacetedRowModel()
                  .rows.filter((row) => row.depth === 0)
                  .map((row) => row.original.resolvedQuantity?.value ?? 0)
                  .reduce((acc, value) => Number(acc) + Number(value), 0);

                return round(Number(sum), 2).toString();
              }
            : undefined,
          minSize: 40,
          meta: {
            canDrop: true,
            menuItems: readOnly
              ? null
              : (tableInstance: Table<TableEntry>) => {
                  return (
                    <>
                      {tableInstance
                        .getSelectedRowModel()
                        .rows.filter((row) => row.depth === 0).length > 0 ? (
                        <>
                          <MenuItem
                            aria-label={'Mass assign quantity definition'}
                            icon={<SparkelIcon icon={faPencil} fixedWidth />}
                            onClick={() =>
                              void setCurrentMassAssignColumn(
                                'resolvedQuantity'
                              )
                            }
                          >
                            {t('set-column-dynamic-value')}
                          </MenuItem>
                          <DynamicQuantitiesModal
                            projectSettings={cleanedProjectSettings}
                            defaultCalculation={null}
                            propertyContext={propertyContext}
                            propertySets={propertySets}
                            isOpen={
                              'resolvedQuantity' === currentMassAssignColumn
                            }
                            onClose={() =>
                              void setCurrentMassAssignColumn(null)
                            }
                            onLink={(calculation, _, unitFormatOverride) => {
                              const updatedRows: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate[] =
                                tableInstance
                                  .getSelectedRowModel()
                                  .rows.map((row) => ({
                                    id: row.original.id,
                                    patch: {
                                      quantity: {
                                        type: QuantityEnum.Dynamic,
                                        calculation: calculation,
                                        value: null,
                                        stringValue: null,
                                        derivedFormula: null,
                                        unitFormatOverride,
                                      },
                                    },
                                  }));
                              updateEntries(updatedRows)
                                .then(() => {
                                  // I've opted for "duplicating" the toast dispatching here, as the `updater` pattern is not accessible from this scope
                                  toast({
                                    status: 'success',
                                    title: t(
                                      'toast.success.update-column-definition'
                                    ),
                                    position: 'top',
                                    duration: 2500,
                                  });
                                })
                                .catch(() => {
                                  toast({
                                    status: 'error',
                                    title: t(
                                      'toast.error.update-column-definition'
                                    ),
                                    position: 'top',
                                  });
                                });
                            }}
                            onUnlink={() => {
                              const updatedRows: UpdateOrderEntryInput[] =
                                tableInstance
                                  .getSelectedRowModel()
                                  .rows.map((row) => ({
                                    id: row.original.id,
                                    patch: {
                                      quantity: null,
                                    },
                                  }));
                              updateEntries(updatedRows)
                                .then(() => {
                                  toast({
                                    status: 'success',
                                    title: t(
                                      'toast.success.clear-column-definition'
                                    ),
                                    position: 'top',
                                    duration: 2500,
                                  });
                                })
                                .catch(() => {
                                  toast({
                                    status: 'error',
                                    title: t(
                                      'toast.error.clear-column-definition'
                                    ),
                                    position: 'top',
                                  });
                                });
                            }}
                            dbIds={[]}
                          />
                        </>
                      ) : null}
                      <Divider />
                      <ShowTotalMenuItem column={quantityColumn} />
                      <Divider />
                      <MenuItem
                        aria-label={`Hide column ${quantityColumn.name}`}
                        icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                        onClick={() => hideColumn(quantityColumn.id)}
                      >
                        {t('hide-column')}
                      </MenuItem>
                    </>
                  );
                },
            columnName: quantityColumn.name,
            projectId: projectId,
            propertySets: propertySets,
            orderColumns: orderColumns,
            isDrawingQuantityColumn,
            activeQuantityCell,
            handleActiveQuantityCellClick,
            isNumeric: true,
            projectSettings: cleanedProjectSettings,
          } as DynamicQuantitiesColumnMeta & SparkelTableColumnMeta,
          cell: DynamicQuantityCell,
        }),
        columnHelper.accessor('unit', {
          header: getTranslatedDefaultColumnName(unitColumn.name, t),
          id: columnTypeToId(ColumnType.Unit),
          size: 100,
          minSize: 40,
          meta: {
            menuItems: readOnly ? null : (
              <MenuItem
                aria-label={`Hide column ${unitColumn.name}`}
                icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                onClick={() => hideColumn(unitColumn.id)}
              >
                {t('hide-column')}
              </MenuItem>
            ),
            // Since we're currently not storing the unit for the resolved quantity, this won't work until we do
            forceUnits: (orderEntry: TableEntry) =>
              !!orderEntry.resolvedQuantity?.units,
            unitSettings,
          },
          cell: UnitCell,
        }),
        columnHelper.accessor('color', {
          header: getTranslatedDefaultColumnName(colorColumn.name, t),
          id: columnTypeToId(ColumnType.Color),
          size: 70,
          minSize: 40,
          meta: {
            menuItems: readOnly ? null : (
              <MenuItem
                aria-label={`Hide column ${colorColumn.name}`}
                icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                onClick={() => hideColumn(colorColumn.id)}
              >
                {t('hide-column')}
              </MenuItem>
            ),
          },
          cell: ColorPicker,
        }),
        columnHelper.accessor(
          (row) => (row.unitPrice ? round(row.unitPrice, 2) : null),
          {
            header: getTranslatedDefaultColumnName(unitPriceColumn.name, t),
            id: columnTypeToId(ColumnType.UnitPrice),

            size: 100,
            minSize: 40,
            meta: {
              menuItems: readOnly ? null : (
                <MenuItem
                  aria-label={`Hide column ${unitPriceColumn.name}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(unitPriceColumn.id)}
                >
                  {t('hide-column')}
                </MenuItem>
              ),
              isNumeric: true,
            } as SparkelTableColumnMeta,
            cell: UnitPriceCell,
          }
        ),
        columnHelper.accessor(
          (row) =>
            typeof row.resolvedQuantity?.value === 'number' && row.unitPrice
              ? row.resolvedQuantity.value * row.unitPrice
              : 0,
          {
            header: getTranslatedDefaultColumnName(totalColumn.name, t),
            size: 100,
            minSize: 40,
            id: columnTypeToId(ColumnType.Total),
            meta: {
              isNumeric: true,
              menuItems: readOnly ? null : (
                <MenuItem
                  aria-label={`Hide column ${totalColumn.name}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(totalColumn.id)}
                >
                  {t('hide-column')}
                </MenuItem>
              ),
            },
            cell: PriceCell,
          }
        ),
        columnHelper.accessor(
          (row) => ({
            count: flatMap(row.resolvedElementIdentifiersDbid ?? [], 'dbIds')
              .length,
            isDynamic: !!row.elementIdentifiersQuery,
          }),
          {
            id: columnTypeToId(ColumnType.LinkedCount),
            header: getTranslatedDefaultColumnName(linkedCountColumn.name, t),
            size: 80,
            minSize: 40,
            meta: {
              menuItems: readOnly ? null : (
                <MenuItem
                  aria-label={`Hide column ${linkedCountColumn.name}`}
                  icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                  onClick={() => hideColumn(linkedCountColumn.id)}
                >
                  {t('hide-column')}
                </MenuItem>
              ),
              onSelectLinkedElements,
              isNumeric: true,
            } as LinkedCountMeta & SparkelTableColumnMeta,
            sortingFn: compareLinkedCount,
            cell: LinkedCountCell,
          }
        ),
      ];
      if (!readOnly || isHistoryDrawerOpen) {
        cols.push(
          columnHelper.display({
            id: addColumnId,
            size: 40,
            minSize: 40,
            enableResizing: false,
            meta: {
              addCustomColumn,
              columns: orderColumns,
              canDrop: true,
            } as AddCustomColumnMeta,
            header: !isHistoryDrawerOpen ? AddCustomColumnHeader : undefined,
            cell: () => {
              return null;
            },
          })
        );
      }
      return cols;
    }, [
      orderColumns,
      readOnly,
      t,
      projectId,
      propertySets,
      isDrawingQuantityColumn,
      activeQuantityCell,
      handleActiveQuantityCellClick,
      unitSettings,
      onSelectLinkedElements,
      hideColumn,
      propertyContext,
      currentMassAssignColumn,
      ShowTotalMenuItem,
      updateEntries,
      toast,
      deleteCustomColumn,
      trackEvent,
      mixpanelProperties,
      addCustomColumn,
    ]);

  const [shouldShowTempRow, setShouldShowTempRow] = useState(false);

  const tableData: TableEntry[] = useMemo<TableEntry[]>(() => {
    if (shouldShowTempRow) {
      const defaultQuantity = {
        type: QuantityEnum.Dynamic,
        calculation: {
          operation: QuantityOperation.Sum,
          attribute: {
            category: 'Sparkel properties',
            name: 'Largest side area',
          },
        },
      };

      const defaultUnit = unitSettings?.area?.units
        ? UnitTypeToUnit(unitSettings.area.units)
        : Unit.M2;

      const colors = colorsByLeastUsed(
        data.map((entry) => entry.color) as string[]
      );
      return [
        ...data,
        {
          id: '',
          name: '',
          quantity: defaultQuantity,
          unit: defaultUnit,
          color: colors[0],
        },
      ];
    }
    return data;
  }, [data, shouldShowTempRow, unitSettings]);

  const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
    `${orderId}-column-resizing`,
    {}
  );

  const tableMeta: EditableTableMeta = useMemo(
    () => ({
      editingRows,
      isEditing: editingRows.length > 0,
      lengthUnit: UnitTypeToUnitSymbol(
        unitSettings?.length?.units ?? LengthUnitTypeEnum.Meters
      ),
    }),
    [editingRows, unitSettings?.length?.units]
  );
  const columnOrder: ColumnOrderState = useMemo(
    () => [
      selectColumnId,
      expanderColumnId,
      ...orderColumns
        .sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0))
        .map(columnToId),
    ],
    [orderColumns]
  );

  const columnVisibility = useMemo(
    () =>
      Object.fromEntries(orderColumns.map((c) => [columnToId(c), c.isVisible])),
    [orderColumns]
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    state: {
      columnOrder,
      columnVisibility,
      columnSizing,
      rowSelection,
      sorting,
      expanded: expanded,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    meta: tableMeta,
    columnResizeMode: 'onChange',
    getSubRows: (row) => row.subRows,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const { getRowModel, getRow, toggleAllRowsSelected, toggleAllRowsExpanded } =
    table;

  // If it changes into a non-dynamic quantity, clear the visualized quantity
  useEffect(() => {
    if (activeQuantityCell !== null) {
      // In this particular case I think the try/catch is justified, since we want to check for an actually removed row
      try {
        const visualizedRow = getRow(activeQuantityCell.rowId);

        const quantity = getQuantityForVisualizedRow(
          activeQuantityCell.columnName,
          visualizedRow,
          orderColumns
        );

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const propertyType = quantity?.calculation!.attribute!
          .name as GeometricProperty;
        const quantityType = quantity?.type;

        if (
          quantityType !== QuantityEnum.Dynamic ||
          activeQuantityCell.quantityType !== propertyType
        ) {
          setVisualizedResults([]);
          setActiveQuantityCell(null);
        }
      } catch (err) {
        // In this case the row has been removed/deleted, so clear the visualized quantity
        setVisualizedResults([]);
        setActiveQuantityCell(null);
      }
    }
  }, [tableData, activeQuantityCell, table, getRow, orderColumns]);

  const selectedRows = useMemo(
    () =>
      Object.entries(rowSelection)
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => {
          try {
            return getRow(id);
          } catch {
            // If the selected row was removed, getRow will fail
            return null;
          }
        })
        .filter((row): row is Row<TableEntry> => !!row),
    // Quickfix - add data as a dependency to make sure the selected rows are updated when data changes
    // Consider rewriting this to not be a usememo on rowSelection
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getRow, rowSelection, data]
  );

  const selectedRootRows = useMemo(
    () => selectedRows.filter((row) => row.depth === 0),
    [selectedRows]
  );

  // Sync selected row IDs with legend context for visual feedback
  const { setSelectedEntryIds } = useLegendEntriesContext();

  useEffect(() => {
    // Use selectedRootRows to get actual entry IDs (not table row indices)
    const selectedIds = new Set(selectedRootRows.map((row) => row.original.id));
    setSelectedEntryIds(orderId, selectedIds);
  }, [selectedRootRows, orderId, setSelectedEntryIds]);

  const {
    currentItemIdSelection: initialItemSelection,
    setCurrentItemIdSelection,
    setSuccessfullyRetrievedCurrentItemIdSelection,
    isGettingCurrentItemIdSelection: shouldSetItemSelectionForSharing,
    currentExpandedItemIds: initialExpandedItemIds,
    setCurrentExpandedItemIds,
    setSuccessfullyRetrievedCurrentExpandedItemIds,
    isGettingCurrentExpandedItemIds: shouldSetExpandedItemSelectionForSharing,
  } = useViewManager();

  // If initialItemSelection is set, set the selection when the component mounts
  useEffect(() => {
    if (initialItemSelection) {
      toggleAllRowsSelected(false);
      const initialSelectionState: RowSelectionState = {};
      initialItemSelection.forEach((id) => {
        initialSelectionState[id] = true;
      });
      setRowSelection(initialSelectionState);
    } else {
      toggleAllRowsSelected(true);
    }
  }, [initialItemSelection, toggleAllRowsSelected]);

  // If initialExpandedItemSelection is set, set the expanded rows when the component mounts
  useEffect(() => {
    if (initialExpandedItemIds) {
      const initialExpandedState: ExpandedState = {};
      initialExpandedItemIds.forEach((id) => {
        initialExpandedState[id] = true;
      });
      setExpanded(initialExpandedState);
    } else {
      toggleAllRowsExpanded(false);
    }
  }, [initialExpandedItemIds, toggleAllRowsExpanded]);

  useEffect(() => {
    // TODO: Handle errors
    if (shouldSetItemSelectionForSharing) {
      const selectedIds = Object.keys(rowSelection).filter(
        (id) => rowSelection[id]
      );
      setCurrentItemIdSelection(selectedIds);
      setSuccessfullyRetrievedCurrentItemIdSelection(true);
    }
  }, [
    rowSelection,
    selectedRootRows,
    setCurrentItemIdSelection,
    setSuccessfullyRetrievedCurrentItemIdSelection,
    shouldSetItemSelectionForSharing,
  ]);

  useEffect(() => {
    // TODO: Handle errors
    if (shouldSetExpandedItemSelectionForSharing) {
      const expandedIds = Object.keys(expanded);
      setCurrentExpandedItemIds(expandedIds);
      setSuccessfullyRetrievedCurrentExpandedItemIds(true);
    }
  }, [
    expanded,
    setCurrentExpandedItemIds,
    setSuccessfullyRetrievedCurrentExpandedItemIds,
    shouldSetExpandedItemSelectionForSharing,
  ]);

  // Update subrow selection when tableData changes
  useEffect(() => {
    selectedRows.forEach((row) => {
      if (row.subRows && row.subRows.length > 0) {
        row.subRows.forEach((subRow) => {
          subRow.toggleSelected(true);
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  const { logEvent } = useEventHistory(projectId);

  const updateSelectedTableEntries = useCallback(
    async (
      updater: (tableEntry: TableEntry) => OrderEntryPatch,
      successMessage: string,
      errorMessage: string
    ) => {
      const entriesToUpdate: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate[] =
        selectedRootRows
          .map((row) => row.original)
          .map((entry) => {
            const patch = updater(entry);
            return {
              id: entry.id,
              patch,
            };
          });
      try {
        await updateEntries(entriesToUpdate);
        toast({
          status: 'success',
          title: successMessage,
          position: 'top',
          duration: 2500,
        });
      } catch (error) {
        console.error(error);
        toast({
          status: 'error',
          title: errorMessage,
          position: 'top',
        });
      }
    },
    [selectedRootRows, toast, updateEntries]
  );

  const resetColumnWidths = () => {
    setColumnSizing({});
  };

  const exportAllRowsToExcel = () => {
    trackEvent(EventName.TableExported, mixpanelProperties);
    return exportToExcel(getRowModel().rows);
  };

  const exportToExcel = async (relevantRows: Row<TableEntry>[]) => {
    const MAX_DECIMALS = 10;
    const excludedHeaders = ['select', 'linkedCount', 'addColumn', 'expander'];
    const relevantHeaders = table
      .getFlatHeaders()
      .filter(
        (header) =>
          header.column.getIsVisible() && !excludedHeaders.includes(header.id)
      );

    const headers: string[] = map(
      relevantHeaders,
      (header) => getColumnName(header.column) ?? header.id
    );
    const customColumnIds = orderColumns
      .filter((column) => column.columnType === ColumnType.Custom)
      .map((column) => column.id);
    const rows: Array<Array<string | null>> = map(relevantRows, (row) =>
      // NOTE: We should look into whether we can redo this function to not be this complex
      // Perhaps by splitting it up to only pass the "resolved map of values", and not doing all the mappings
      // eslint-disable-next-line complexity
      map(relevantHeaders, (header) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const value = row.getValue<any>(header.id);

        if (value && customColumnIds.includes(header.id)) {
          if (getColumnName(header.column) === 'SKU') {
            return value;
          }
          if (!isNaN(value) && !isNaN(parseFloat(value))) {
            return Math.round(parseFloat(value) * MAX_DECIMALS) / MAX_DECIMALS;
          } else {
            return value;
          }
        } else if (
          !isNil(value) &&
          typeof value === 'object' &&
          !isNil(value.value)
        ) {
          const innerValue =
            typeof value.value === 'number'
              ? value.value
              : Number.parseFloat(value.value);
          if (!isNaN(innerValue)) {
            return Math.round(innerValue * MAX_DECIMALS) / MAX_DECIMALS;
          } else {
            return innerValue;
          }
        } else if (!isNil(value)) {
          return typeof value === 'number'
            ? Math.round(value * MAX_DECIMALS) / MAX_DECIMALS
            : value.toString();
        } else {
          return null;
        }
      })
    );

    exportTableToExcel({
      headers: headers,
      rows: rows,
      filename: `Sparkel Export - ${title} - ${
        projectData?.project?.name
      } - ${new Date().toUTCString()}.xlsx`,
    });
  };

  const { tenantTier, tenant, isProtan } = useUserTenant();
  const enablePayment = useFlag('enable-payments');

  const maxItemsReached = useMemo(() => {
    const numberOfItems = data.length;
    return (
      tenantTier === 'FREE' &&
      enablePayment &&
      numberOfItems >= tierLimits.free.MAX_ITEMS_PER_TABLE
    );
  }, [data.length, enablePayment, tenantTier]);

  const {
    isOpen: buyModalIsOpen,
    onOpen: openBuyModal,
    onClose: closeBuyModal,
  } = useDisclosure();

  const handleAddItemClick = async () => {
    if (maxItemsReached) {
      openBuyModal();
      return;
    }

    setShouldShowTempRow(!shouldShowTempRow);
  };

  /*
  useEffect(() => {
    toggleAllRowsSelected(true);
  }, [toggleAllRowsSelected]);
  */

  const selectedRowDbIds: DbIdsPerModel = useMemo(() => {
    let ids: DbIdsPerModel = {};
    for (const row of selectedRows) {
      if (row.getParentRow()?.getIsSelected()) {
        continue;
      }
      ids = mergeRecordOfLists(
        ids,
        fromPairs(
          row.original.resolvedElementIdentifiersDbid?.map(
            ({ modelUrn, dbIds }) => [modelUrn, dbIds]
          )
        )
      );
    }
    return ids;
  }, [selectedRows]);

  const { hide } = useVisibility();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const { visibilityManager: sheetsViewerV2VisibilityManager } =
    useSheetViewerV2();

  const hideElementsForRows = useCallback(() => {
    hide(selectedRowDbIds);
  }, [selectedRowDbIds, hide]);

  const handleDeleteEntries = useCallback(async () => {
    let entryIdsToDelete: string[] = [];
    for (const row of selectedRootRows) {
      invariant(row.original.id, 'Tried to invoke delete on row without id');
      entryIdsToDelete.push(row.original.id);
    }
    await deleteEntries(entryIdsToDelete);
    setRowSelection(
      omit(
        rowSelection,
        selectedRows.map((row) => row.id)
      )
    );

    logEvent({
      eventName: 'Items Deleted',
      eventType: EventType.Deleted,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [entryIdsToDelete],
        details: `${selectedRootRows.length} item${
          selectedRootRows.length === 1 ? '' : 's'
        } deleted in ${title}`,
      },
    });
  }, [
    deleteEntries,
    logEvent,
    rowSelection,
    selectedRootRows,
    selectedRows,
    title,
  ]);

  const [isIsolationToggleActive, setIsolationToggleActive] =
    useRowHighlightingEffect(selectedRowDbIds, selectedRows, table);

  const linkElementsToRow = useCallback(() => {
    trackEvent(EventName.ElementLinkedToItem, mixpanelProperties);

    logEvent({
      eventName: 'Elements Linked to Items',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `${
          Object.values(selectedDbIdsWithoutLegends).flat().length
        } elements linked to ${selectedRootRows.length} items in ${title}`,
      },
    });

    return updateSelectedTableEntries(
      (entry) => ({
        ...getUpdatedDbidFields(
          mergeRecordOfLists(
            fromPairs(
              entry.resolvedElementIdentifiersDbid?.map(
                ({ modelUrn, dbIds }) => [modelUrn, dbIds]
              )
            ),
            selectedDbIdsWithoutLegends
          )
        ),
        usePromptForLinking: false,
        promptForLinking: null,
      }),
      t('toast.success.link-elements'),
      t('toast.error.link-elements')
    );
  }, [
    logEvent,
    mixpanelProperties,
    selectedDbIdsWithoutLegends,
    selectedRootRows,
    t,
    title,
    trackEvent,
    updateSelectedTableEntries,
  ]);
  const unlinkElementsFromRow = useCallback(() => {
    trackEvent(EventName.ElementUnlinkedFromItem, mixpanelProperties);

    logEvent({
      eventName: 'Elements Unlinked from Items',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `${
          Object.values(selectedDbIdsWithoutLegends).flat().length
        } elements unlinked from ${selectedRootRows.length} items in ${title}`,
      },
    });

    return updateSelectedTableEntries(
      (entry) => ({
        ...getUpdatedDbidFields(
          fromPairs(
            removeElementsFromRecords(
              entry.resolvedElementIdentifiersDbid ?? [],
              selectedDbIdsWithoutLegends
            ).map(({ modelUrn, dbIds }) => [modelUrn, dbIds])
          )
        ),
        usePromptForLinking: false,
        promptForLinking: null,
      }),
      t('toast.success.unlink-elements'),
      t('toast.error.unlink-elements')
    );
  }, [
    logEvent,
    mixpanelProperties,
    selectedDbIdsWithoutLegends,
    selectedRootRows,
    t,
    title,
    trackEvent,
    updateSelectedTableEntries,
  ]);

  const unlinkAllElementsFromRow = () => {
    trackEvent(EventName.ElementUnlinkedFromItem, mixpanelProperties);

    logEvent({
      eventName: 'Elements Unlinked from Items',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `All elements unlinked from ${selectedRootRows.length} items in ${title}`,
      },
    });

    return updateSelectedTableEntries(
      () => ({
        elementIdentifiersDbid: null,
        elementIdentifiersQuery: null,
        usePromptForLinking: false,
        promptForLinking: null,
      }),
      t('toast.success.unlink-elements'),
      t('toast.error.unlink-elements')
    );
  };

  const { registerItem, unregisterItem } = useForgeContextMenuContext();
  const {
    registerItem: registerSheetItem,
    unregisterItem: unregisterSheetItem,
  } = useSheetContextMenuContext();

  useEffect(() => {
    if (readOnly) {
      return;
    }

    const linkItem = {
      id: 'linkToSelectedResources',
      label: t('link-to-selected-items'),
      group: 'items',
      icon: <SparkelIcon size="sm" icon={faChain} fixedWidth />,
      hidden: () =>
        !(selectedRootRows.length > 0 && hasDbIds(selectedDbIdsWithoutLegends)),
      onClick: linkElementsToRow,
    };
    const unlinkItem = {
      id: 'unlinkFromResources',
      label: t('unlink-from-selected-items'),
      group: 'items',
      icon: <SparkelIcon size="sm" icon={faChainSlash} fixedWidth />,
      hidden: () =>
        !(selectedRootRows.length > 0 && hasDbIds(selectedDbIdsWithoutLegends)),
      onClick: unlinkElementsFromRow,
    };

    registerItem(linkItem);
    registerItem(unlinkItem);
    registerSheetItem(linkItem);
    registerSheetItem(unlinkItem);

    return () => {
      unregisterItem('linkToResources');
      unregisterItem('unlinkFromResources');
      unregisterSheetItem('linkToResources');
      unregisterSheetItem('unlinkFromResources');
    };
  }, [
    linkElementsToRow,
    readOnly,
    registerItem,
    registerSheetItem,
    selectedDbIdsWithoutLegends,
    selectedRootRows,
    selectedRowDbIds,
    t,
    unlinkElementsFromRow,
    unregisterItem,
    unregisterSheetItem,
  ]);

  const {
    register: registerToEditForm,
    reset: resetEditForm,
    handleSubmit: handleSubmitEditForm,
    setValue: setEditFormValue,
    getValues: getEditFormValues,
    watch: watchEditForm,
    formState: {
      isSubmitting: isSubmittingEditForm,
      errors: errorsEditForm,
      ...restEditFormState
    },
    control: editControl,
    ...restEditForm
  } = useForm<EditableRowsFormValues>({});

  const [, createOrderEntryMutation] = useMutation(CreateOrderEntryDocument);

  const createOrderEntry = async (orderEntryInput: CreateOrderEntryInput) => {
    const result = await createOrderEntryMutation({
      input: orderEntryInput,
    });
    if (result.error) {
      console.error(result.error);
      toast({
        status: 'error',
        title: t('toast.error.create-item'),
        position: 'top',
      });
    } else {
      const created = result.data?.createOrderEntry?.orderEntry;
      if (created) {
        setNewEntryId(created.id);
      }
      toast({
        status: 'success',
        position: 'top',
        title: t('toast.success.create-item'),
      });

      return created?.id;
    }
  };

  const [, executeUpdateOrderMutation] = useMutation(UpdateOrderDocument);
  const handleCreateSuggestedResources = useCallback(
    async (suggested: SuggestionInfo[]) => {
      invariant(tableData.length === 0, 'Table data is not empty');

      // Create any missing columns
      const missingColumns: string[] = [];
      for (const field of suggested[0].customFields) {
        if (!orderColumns.find((column) => column.name === field.name)) {
          missingColumns.push(field.name);
        }
      }
      const titleOrder =
        orderColumns.find(
          (column) => column.columnType === ColumnType.Description
        )?.columnOrder ?? 0;
      let newColumnOrders = orderColumns
        .filter(
          (column) => column.columnOrder && column.columnOrder > titleOrder
        )
        .map((column) => ({
          name: column.name,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          columnOrder: column.columnOrder! + missingColumns.length,
          columnType: column.columnType,
        }));
      if (missingColumns.length === 0) {
        newColumnOrders = [];
      }

      const updateOrderInput: UpdateOrderInput = {
        id: orderId,
        patch: {
          orderColumns: {
            create: missingColumns.map((name, index) => ({
              columnType: ColumnType.Custom,
              name,
              columnOrder: titleOrder + index + 1,
            })),
            updateByNameAndColumnTypeAndOrderId: newColumnOrders.map(
              (column) => ({
                name: column.name,
                orderId,
                columnType: column.columnType,
                patch: {
                  columnOrder: column.columnOrder,
                },
              })
            ),
          },
          orderEntries: {
            create: suggested.map((suggestedResource) => {
              return {
                name: suggestedResource.resourceName,
                color: COLORS[0],
                elementIdentifiersDbid: suggestedResource.dbIds,
                currency: suggestedResource.currency,
                unit: suggestedResource.unit,
                quantity: suggestedResource.quantity,
                customFields: {
                  create: suggestedResource.customFields.map(
                    (
                      field
                    ): CustomFieldsOrderEntryIdFkeyCustomFieldsCreateInput => ({
                      orderColumn: {
                        connectByNameAndColumnTypeAndOrderId: {
                          name: field.name,
                          orderId: orderId,
                          columnType: ColumnType.Custom, // TODO: Need to ensure that this' correct
                        },
                      },
                      value: {
                        type: field.type,
                        calculation: field.calculation,
                        stringValue: field.staticValue,
                      },
                    })
                  ),
                },
              };
            }),
          },
        },
      };

      const updateOrderResult = await executeUpdateOrderMutation({
        input: updateOrderInput,
      });

      if (updateOrderResult.error) {
        console.error(updateOrderResult.error);
        toast({
          status: 'error',
          title: t('toast.error.create-item'),
          position: 'top',
        });
        return;
      } else {
        toast({
          status: 'success',
          title: t('toast.success.create-item'),
          position: 'top',
        });
      }
    },
    [
      tableData.length,
      orderColumns,
      orderId,
      executeUpdateOrderMutation,
      toast,
      t,
    ]
  );

  const onSubmitForm = handleSubmitEditForm(async (formValues) => {
    invariant(
      editingRows.length > 0,
      'Trying to submit when no row is being edited'
    );
    if (shouldShowTempRow) {
      invariant(
        editingRows.length === 1,
        'Only the temporary row should be editable'
      );

      const newFormValues = formValues[editingRows[0]];

      invariant(
        newFormValues.name,
        'Trying to submit without required item name'
      );

      const created = await createOrderEntry({
        orderEntry: {
          name: newFormValues.name,
          orderId: orderId,
          unit: newFormValues.unit,
          color: newFormValues.color,
          quantity: newFormValues.quantity,
          //groupByAttributes: entry.groupByAttributes,
          unitPrice: newFormValues.unitPrice,
          currency: newFormValues.currency,
          elementIdentifiersDbid: dbIdsPerModelToInput(selectedDbIds),
        },
      });

      if (created) {
        setEditingRows([]);
        trackEvent(EventName.ItemAdded, {
          ...mixpanelProperties,
          'Item Name': newFormValues.name,
          'From Template': false,
          'From Suggestion': false,
        });
        logEvent({
          eventName: 'Item Added',
          eventType: EventType.Created,
          resourceName: title,
          resourceType: EventResource.OrderEntry,
          metadata: {
            affectedResources: [created],
            details: `Added ${newFormValues.name} to ${title}`,
          },
        });
      } else {
        toast({
          status: 'error',
          title: t('toast.error.create-item'),
          position: 'top',
        });
      }

      setShouldShowTempRow(false);

      return;
    }

    try {
      await updateEntries(
        editingRows.map((rowId) =>
          toUpdateEntryInput(
            formValues[rowId],
            orderColumns,
            getRow(rowId).original.id
          )
        )
      );
    } catch (e) {
      console.error(e);
      toast({
        status: 'error',
        title: t('toast.error.update-item'),
        position: 'top',
      });
    } finally {
      logEvent({
        eventName: 'Items Updated',
        eventType: EventType.Modified,
        resourceName: title,
        resourceType: EventResource.OrderEntry,
        metadata: {
          affectedResources: [
            editingRows.map((rowId) => getRow(rowId).original.id),
          ],
          details: `Updated ${editingRows.length} item${
            editingRows.length === 1 ? '' : 's'
          } in ${title}`,
        },
      });
    }
    setEditingRows([]);
  });

  const handleEditRows = useCallback(
    (rowsToEdit: Row<TableEntry>[]) => {
      invariant(rowsToEdit.length > 0, 'Cannot edit when 0 selected rows');
      const formInput: [string, EditableRowFormValues][] = rowsToEdit.map(
        (row) => [
          row.id,
          {
            name: row.original.name,
            currency: row.original.currency,
            unit: row.original.unit,
            color: row.original.color,
            unitPrice: row.original.unitPrice,
            custom: toCustomFormData(row.original.customFields, orderColumns),
            quantity: toQuantityFormData(
              row.original.quantity,
              row.original.resolvedQuantity,
              orderColumns
            ),
          },
        ]
      );

      resetEditForm(Object.fromEntries(formInput));
      setEditingRows(rowsToEdit.map((row) => row.id));
    },
    [orderColumns, resetEditForm]
  );

  useEffect(() => {
    if (shouldShowTempRow) {
      const allRows = getRowModel().rows;
      const newRow = [allRows[allRows.length - 1]];
      handleEditRows(newRow);
      setNewRowId((allRows.length - 1).toString());
    }
  }, [shouldShowTempRow, getRowModel, handleEditRows]);

  const handleEditRowClick = () => {
    handleEditRows(selectedRootRows);
  };

  useHotkeys(
    'shift+a',
    () => {
      handleAddItemClick();
    },
    {
      enabled: !shouldShowTempRow,
    }
  );

  const [newEntryId, setNewEntryId] = useState<string | null>(null);
  const [newRowId, setNewRowId] = useState<string | undefined>(undefined);
  const [entryIdToBeUpdated, setEntryIdToBeUpdated] = useState<string | null>(
    null
  );
  const newRowRef = useRef<HTMLDivElement | null>(null);

  const newRow = useMemo(() => {
    const newRow = tableData.find((entry) => entry.id === newEntryId);
    if (newRow) {
      return getRowModel().rows.find((row) => row.original.id === newEntryId);
    }
  }, [tableData, getRowModel, newEntryId]);

  // Select and scroll to new entry when it is created
  useEffect(() => {
    if (newRow) {
      toggleAllRowsSelected(false);
      newRow.toggleSelected(true);
      if (newRowRef.current) {
        newRowRef.current.scrollIntoView({ block: 'center' });
      }

      setNewEntryId(null);
    }
  }, [newRow, toggleAllRowsSelected]);

  const handleCancelEdit = () => {
    if (entryIdToBeUpdated) {
      deleteEntries([entryIdToBeUpdated]);
      setEntryIdToBeUpdated(null);
    }

    setShouldShowTempRow(false);
    setEditingRows([]);
  };

  const handleGroupBySubmit = (attributes: ForgeAttributeType[]) => {
    logEvent({
      eventName: 'Item Breakdown Updated',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `Updated breakdown for ${selectedRootRows.length} item${
          selectedRootRows.length === 1 ? '' : 's'
        } in ${title}`,
      },
    });
    trackEvent(EventName.ItemGrouped, {
      ...mixpanelProperties,
      Properties: attributes,
    });

    return updateSelectedTableEntries(
      () => ({ groupByAttributes: attributes }),
      t('toast.success.breakdown-items'),
      t('toast.error.breakdown-items')
    );
  };

  const [, flattenSubrows] = useMutation(FlattenSubrowsDocument);

  async function flattenForOrderEntry(entry: TableEntry) {
    if (!entry.subRows || entry.subRows.length === 0) {
      return;
    }
    invariant(entry.id, 'Trying to flatten entry without id');
    const colors = colorsByLeastUsed(
      getRowModel().rows.map((row) => row.original.color) as string[]
    );

    const { error } = await flattenSubrows({
      input: {
        theOrderEntryId: entry.id,
        subrows: entry.subRows.map((subrow, index) => ({
          name: `${subrow.name}`,
          color: colors[index % colors.length],
          elementIdentifiersDbid: subrow.resolvedElementIdentifiersDbid,
          elementIdentifiersQuery: subrow.elementIdentifiersQuery,
        })),
      },
    });
    if (error) {
      throw error;
    }
  }

  const handleFlattenSubmit = async () => {
    const flattenTasks = selectedRootRows.map((row) =>
      flattenForOrderEntry(row.original)
    );

    try {
      await Promise.all(flattenTasks);
      toast({
        status: 'success',
        title: t('toast.success.flatten-items'),
        position: 'top',
        duration: 2500,
      });

      trackEvent(EventName.ItemFlattened, mixpanelProperties);
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: t('toast.error.flatten-items'),
        position: 'top',
      });
    } finally {
      logEvent({
        eventName: 'Items Flattened',
        eventType: EventType.Modified,
        resourceName: title,
        resourceType: EventResource.OrderEntry,
        metadata: {
          affectedResources: [selectedRootRows.map((row) => row.original.id)],
          details: `Flattened ${selectedRootRows.length} items in ${title}`,
        },
      });
    }
  };

  function duplicateResource(
    resourceName: string,
    entry: TableEntry,
    color: string,
    useCurrentSelection?: boolean
  ) {
    invariant(entry.id, 'Entry id not provided');
    invariant(entry.name, 'Description not provided');

    return createOrderEntry({
      orderEntry: {
        name: resourceName,
        orderId: orderId,
        unit: entry.unit,
        color,
        quantity: entry.quantity,
        groupByAttributes: entry.groupByAttributes,
        unitPrice: entry.unitPrice,
        currency: entry.currency,
        elementIdentifiersQuery: useCurrentSelection
          ? undefined
          : entry.elementIdentifiersQuery,
        customFields: {
          create: entry.customFields?.map((field) => ({
            value: field.value,
            orderColumnId: field.orderColumnId,
          })),
        },
        elementIdentifiersDbid: useCurrentSelection
          ? dbIdsPerModelToInput(selectedDbIds)
          : entry.elementIdentifiersDbid,
        usePromptForLinking: entry.usePromptForLinking,
        promptForLinking: entry.promptForLinking,
      },
    });
  }

  const [, moveOrCopyOrderEntries] = useMutation(
    MoveOrCopyOrderEntriesDocument
  );

  const handleMoveOrCopyItemsClick = async (targetOrderId: string) => {
    const moveOrCopyInput: MoveOrCopyOrderEntriesInput = {
      orderEntryIds: selectedRootRows.map((row) => row.original.id),
      targetOrderId: targetOrderId,
      copyEntries: true,
    };

    const result = await moveOrCopyOrderEntries({ input: moveOrCopyInput });
    if (result.error) {
      console.error(result.error);
      toast({
        status: 'error',
        position: 'top',
        title: t('toast.error.copy-items'),
      });
    } else {
      toast({
        status: 'success',
        position: 'top',
        title: t('toast.success.copy-items'),
      });

      trackEvent(EventName.ItemsMovedOrCopied, mixpanelProperties);

      const targetOrder = projectData?.project?.orders.find(
        (order) => order.id === targetOrderId
      );

      logEvent({
        eventName: 'Items Copied to Another Table',
        eventType: EventType.Created,
        resourceName: title,
        resourceType: EventResource.OrderEntry,
        metadata: {
          affectedResources: [selectedRootRows.map((row) => row.original.id)],
          details: `Copied ${selectedRootRows.length} item${
            selectedRootRows.length === 1 ? '' : 's'
          } to ${targetOrder?.name || ''}`,
        },
      });
    }
  };

  const handleDuplicateClick = async () => {
    const rows = getRowModel().rows.map((row) => row.original);
    const existingNames = rows.map((row) => row.name);
    const colors = colorsByLeastUsed(
      data.map((entry) => entry.color) as string[]
    );
    const duplicateTasks = selectedRootRows.map((row, index) => {
      const newResourceName = findNextAvailableNameForCopy(
        row.original.name,
        existingNames
      );
      existingNames.push(newResourceName); // To prevent duplicates when starting multiple threads
      duplicateResource(
        newResourceName,
        row.original,
        colors[index % colors.length]
      );
    });

    try {
      await Promise.all(duplicateTasks);
      toast({
        status: 'success',
        title: t('toast.success.duplicate-item'),
        position: 'top',
        duration: 2500,
      });

      trackEvent(EventName.ItemDuplicated, mixpanelProperties);
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: t('toast.error.duplicate-item'),
        position: 'top',
      });
    } finally {
      logEvent({
        eventName: 'Items Duplicated',
        eventType: EventType.Created,
        resourceName: title,
        resourceType: EventResource.OrderEntry,
        metadata: {
          affectedResources: [selectedRootRows.map((row) => row.original.id)],
          details: `Duplicated ${selectedRootRows.length} item${
            selectedRootRows.length === 1 ? '' : 's'
          } in ${title}`,
        },
      });
    }
  };

  const [, createOrderEntryFromTemplate] = useMutation(
    CreateOrderEntryFromTemplateDocument
  );

  const handleCreateFromTemplateClick = async (
    templateId: string,
    newName: string,
    color: string,
    query: ForgeAttributeQueryTypeInput | null,
    dbids: ElementIdentifierDbidTypeInput[] | null
  ) => {
    const colors = colorsByLeastUsed(
      data.map((entry) => entry.color) as string[]
    );

    const input: CreateOrderEntryFromTemplateInput = {
      orderEntryTemplateId: templateId,
      orderId,
      title: newName,
      color: color || colors[0],
      query,
      dbids: dbids ? dbids : dbIdsPerModelToInput(selectedDbIds),
    };
    const result = await createOrderEntryFromTemplate({ input });
    if (result.error) {
      console.error(result.error);
      toast({
        status: 'error',
        position: 'top',
        title: t('toast.error.create-item'),
      });
    } else {
      const created = result.data?.createOrderEntryFromTemplate?.orderEntry;
      if (created) {
        const allRows = getRowModel().rows;

        setNewEntryId(created.id);
        setNewRowId(allRows.length.toString());
      }
      toast({
        status: 'success',
        position: 'top',
        title: t('toast.success.create-item'),
      });

      trackEvent(EventName.ItemAdded, {
        ...mixpanelProperties,
        'Item Name': title,
        'From Template': true,
        'From Suggestion': false,
      });

      logEvent({
        eventName: 'Item Added',
        eventType: EventType.Created,
        resourceName: title,
        resourceType: EventResource.OrderEntry,
        metadata: {
          affectedResources: [created?.id],
          details: `Added ${newName} to ${title}`,
        },
      });
    }
  };

  const [, createResourceTemplate] = useMutation(
    CreateTemplateFromOrderEntryDocument
  );

  const handleCreateTemplateClick = async (templateName: string) => {
    const createTemplateTasks = selectedRootRows.map((row) =>
      createResourceTemplate({ orderEntryId: row.original.id, templateName })
    );

    try {
      await Promise.all(createTemplateTasks);
      toast({
        status: 'success',
        title: t('toast.success.create-item-template'),
        position: 'top',
        duration: 2500,
      });
      trackEvent(EventName.ItemTemplateCreated, {
        ...mixpanelProperties,
        'Item Template': templateName,
      });
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: t('toast.error.create-item-template'),
        position: 'top',
      });
    }
  };

  const handleRemoveGroupBy = () => {
    logEvent({
      eventName: 'Item Breakdown Updated',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `Updated breakdown for ${selectedRootRows.length} item${
          selectedRootRows.length === 1 ? '' : 's'
        } in ${title}`,
      },
    });
    return updateSelectedTableEntries(
      () => ({ groupByAttributes: null }),
      t('toast.success.clear-breakdown'),
      t('toast.error.clear-breakdown')
    );
  };

  const handleDynamicLinkingSubmit = (query: ForgeAttributeQueryTypeInput) => {
    trackEvent(EventName.ElementLinkedToItem, {
      ...mixpanelProperties,
      Query: query,
    });

    query.predicates = query.predicates.sort((a) =>
      a?.attribute &&
      (a.attribute.category === SPARKEL_GEOMETRIC_PROPERTY_SET ||
        a.attribute.category === SPARKEL_LOCATION_PROPERTY_SET)
        ? -1
        : 1
    );

    if (
      query.predicates[query.predicates.length - 1]?.attribute.category ===
        SPARKEL_GEOMETRIC_PROPERTY_SET ||
      query.predicates[query.predicates.length - 1]?.attribute.category ===
        SPARKEL_LOCATION_PROPERTY_SET
    ) {
      toast({
        status: 'error',
        title: t('toast.error.update-dynamic-link'),
        description: 'Filters cannot exclusivly use calculated quantities',
        position: 'top',
      });
      return Promise.reject(
        'Filters cannot exclusivly use calculated quantities'
      );
    }

    logEvent({
      eventName: 'Item Dynamic Link Updated',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `Updated dynamic link for ${selectedRootRows.length} item${
          selectedRootRows.length === 1 ? '' : 's'
        } in ${title}`,
      },
    });

    return updateSelectedTableEntries(
      () => ({
        elementIdentifiersQuery: query,
        // Setting a query clears statically linked dbIds
        elementIdentifiersDbid: null,
      }),
      t('toast.success.link-elements'),
      t('toast.error.link-elements')
    );
  };

  const handleRemoveDynamicLinking = () => {
    logEvent({
      eventName: 'Item Dynamic Link Updated',
      eventType: EventType.Modified,
      resourceName: title,
      resourceType: EventResource.OrderEntry,
      metadata: {
        affectedResources: [selectedRootRows.map((row) => row.original.id)],
        details: `Removed dynamic link for ${selectedRootRows.length} item${
          selectedRootRows.length === 1 ? '' : 's'
        } in ${title}`,
      },
    });
    return updateSelectedTableEntries(
      () => ({
        elementIdentifiersQuery: null,
        usePromptForLinking: false,
        promptForLinking: null,
      }),
      t('toast.success.unlink-elements'),
      t('toast.error.unlink-elements')
    );
  };

  const handleClearPromptLinking = useCallback(() => {
    return updateSelectedTableEntries(
      () => ({
        usePromptForLinking: false,
        promptForLinking: null,
      }),
      t('toast.success.unlink-elements'),
      t('toast.error.unlink-elements')
    );
  }, [t, updateSelectedTableEntries]);

  const resizeableAreaRef = useRef<HTMLDivElement>(null);

  const rowColors = getRowModel()
    .rows.map((e) => e.original.color)
    .filter((e) => e) as string[];

  const [activeDropColumnData, setActiveDropColumnData] = useState<{
    columnId: string;
    property: {
      category: string;
      propertyName: string;
      displayName: string;
    };
  } | null>(null);

  const clearActiveDropColumnData = useCallback(() => {
    setActiveDropColumnData(null);
  }, [setActiveDropColumnData]);

  const onColumnDrop = useCallback(
    (columnId: string) => (event: DragEvent) => {
      console.debug(
        'dropping',
        event.dataTransfer.getData(PROPERTY_DRAG_DATA_FORMAT),
        'onto',
        columnId
      );
      const rawDragData = event.dataTransfer.getData(PROPERTY_DRAG_DATA_FORMAT);
      const dragData = parseDragData(rawDragData);

      if (dragData === null) {
        throw new Error(`Received invalid drop data: ${rawDragData}`);
      }

      setActiveDropColumnData({
        columnId,
        property: dragData,
      });
    },
    []
  );

  const onColumnRename = (columnId: string, name: string) => {
    return saveColumns([
      {
        id: columnIdToOrderColumnId(columnId, orderColumns),
        name,
      },
    ]);
  };

  const handleConfirmColumnDrop = useCallback(
    async (isSum: boolean, columnName?: string) => {
      invariant(activeDropColumnData !== null, 'No active drop column data');

      trackEvent(EventName.PropertyAssignedToColumn, {
        ...mixpanelProperties,
        Property: {
          category: activeDropColumnData.property.category,
          name: activeDropColumnData.property.propertyName,
        },
      });

      const quantityOperation = isSum
        ? QuantityOperation.Sum
        : QuantityOperation.Extract;

      if (activeDropColumnData.columnId === 'quantity') {
        updateEntries(
          data.map((entry) => ({
            id: entry.id,
            patch: {
              quantity: {
                type: QuantityEnum.Dynamic,
                calculation: {
                  operation: quantityOperation,
                  attribute: {
                    category: activeDropColumnData.property.category,
                    name: activeDropColumnData.property.propertyName,
                  },
                },
              },
            },
          }))
        );

        logEvent({
          eventName: 'Property Assigned to Column',
          eventType: EventType.Modified,
          resourceName: title,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [activeDropColumnData.columnId],
            details: `Assigned property ${activeDropColumnData.property.propertyName} to ${columnName} column in ${title}`,
          },
        });
      } else if (activeDropColumnData.columnId === 'addColumn') {
        invariant(columnName !== undefined, 'No column name provided');
        // Add custom column, then update entries
        const newColumnId = await addCustomColumn(columnName);

        trackEvent(EventName.CustomColumnAdded, {
          ...mixpanelProperties,
          'Column Name': columnName,
        });

        updateEntries(
          data.map((entry) => {
            return {
              id: entry.id,
              patch: {
                customFields: {
                  create: [
                    {
                      orderColumnId: newColumnId,
                      orderEntryId: entry.id,
                      value: {
                        type: QuantityEnum.Dynamic,
                        calculation: {
                          operation: quantityOperation,
                          attribute: {
                            category: activeDropColumnData.property.category,
                            name: activeDropColumnData.property.propertyName,
                          },
                        },
                      },
                    },
                  ],
                },
              },
            };
          })
        );
      } else {
        updateEntries(
          data.map((entry) => {
            const hasCustomField = entry.customFields?.some(
              (customField) =>
                customField.orderColumnId === activeDropColumnData.columnId
            );

            let entryInput: OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate;
            if (!hasCustomField) {
              entryInput = {
                id: entry.id,
                patch: {
                  customFields: {
                    create: [
                      {
                        orderColumnId: activeDropColumnData.columnId,
                        orderEntryId: entry.id,
                        value: {
                          type: QuantityEnum.Dynamic,
                          calculation: {
                            operation: quantityOperation,
                            attribute: {
                              category: activeDropColumnData.property.category,
                              name: activeDropColumnData.property.propertyName,
                            },
                          },
                        },
                      },
                    ],
                  },
                },
              };
            } else {
              entryInput = {
                id: entry.id,
                patch: {
                  customFields: {
                    updateByOrderColumnIdAndOrderEntryId: [
                      {
                        orderColumnId: activeDropColumnData.columnId,
                        orderEntryId: entry.id,
                        patch: {
                          value: {
                            type: QuantityEnum.Dynamic,
                            calculation: {
                              operation: quantityOperation,
                              attribute: {
                                category:
                                  activeDropColumnData.property.category,
                                name: activeDropColumnData.property
                                  .propertyName,
                              },
                            },
                          },
                        },
                      },
                    ],
                  },
                },
              };
            }
            return entryInput;
          })
        );

        logEvent({
          eventName: 'Property Assigned to Column',
          eventType: EventType.Modified,
          resourceName: title,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [activeDropColumnData.columnId],
            details: `Assigned property ${activeDropColumnData.property.propertyName} to ${columnName} column in ${title}`,
          },
        });
      }

      setActiveDropColumnData(null);
    },
    [
      activeDropColumnData,
      trackEvent,
      mixpanelProperties,
      logEvent,
      title,
      updateEntries,
      data,
      addCustomColumn,
    ]
  );

  let resourceSuggestionsNode: React.ReactNode = null;

  if (tableData.length === 0 && suggestedResources === null) {
    resourceSuggestionsNode = (
      <Flex
        flex={1}
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
        py={2}
      >
        <PlaceholderText>
          {readOnly || isProtan
            ? t('no-items.placeholder')
            : t('no-items.suggestions-placeholder')}
        </PlaceholderText>
      </Flex>
    );
  } else if (
    tableData.length === 0 &&
    suggestedResources !== null &&
    !readOnly
  ) {
    resourceSuggestionsNode = (
      <Flex
        flex={1}
        alignItems="stretch"
        flexDirection="column"
        justifyContent="center"
        py={2}
      >
        <SuggestedResourceList
          suggestedResources={suggestedResources}
          onAddSuggestedResources={handleCreateSuggestedResources}
        />
      </Flex>
    );
  }

  const panelRef = useRef<HTMLDivElement>(null);
  const DEFAULT_PANEL_HEIGHT = 300;

  const [panelHeight, setPanelHeight] = useLocalStorage<number>(
    'order-entries-panel-height',
    DEFAULT_PANEL_HEIGHT
  );

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = panelRef.current?.clientHeight || 0;

    let newHeight = startHeight;

    const doResize = (mouseMoveEvent: MouseEvent) => {
      newHeight = Math.max(
        startHeight - (mouseMoveEvent.clientY - startY),
        100
      );
      if (panelRef.current) {
        panelRef.current.style.height = `${newHeight}px`;
      }
    };

    const stopResize = () => {
      setPanelHeight(newHeight);
      window.removeEventListener('mousemove', doResize);
      window.removeEventListener('mouseup', stopResize);
    };

    window.addEventListener('mousemove', doResize);
    window.addEventListener('mouseup', stopResize);
  };

  // Clear inline height style when entering fullscreen mode
  // (inline styles from resizing would otherwise override Chakra's height prop)
  useEffect(() => {
    if (isFullscreen && panelRef.current) {
      panelRef.current.style.height = '';
    }
  }, [isFullscreen]);

  const showFooter = orderColumns.some(
    (column) => column.isVisible && column.showTotal
  );

  return (
    <>
      <GridItem
        area="viewer-interactions-end / viewer-interactions-end / main-end / main-end"
        ref={resizeableAreaRef}
        visibility="hidden"
      />
      <OnDemandGeometryVisualization operationResults={visualizedResults} />

      <GridItem
        zIndex={isFullscreen ? 20 : 9}
        area={isFullscreen ? undefined : 'main'}
        gridColumn={isFullscreen ? 2 : undefined}
        gridRow={isFullscreen ? '1 / -1' : undefined}
        justifySelf="center"
        maxWidth="100%"
        maxHeight="100%"
        overflow="auto"
        alignSelf={isFullscreen ? 'stretch' : 'end'}
        width={isFullscreen ? '100%' : undefined}
        height={isFullscreen ? '100%' : undefined}
        paddingY={isFullscreen ? 2 : undefined}
        sx={{
          '@media (max-width: 1200px)': {
            paddingRight: 2,
          },
        }}
      >
        <Panel
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          marginBottom={isFullscreen ? 0 : 3}
          paddingBottom={0}
          height={isFullscreen ? '100%' : undefined}
        >
          {!isFullscreen && (
            <Box
              onMouseDown={handleMouseDown}
              style={{ touchAction: 'none' }}
              cursor="ns-resize"
              height="2"
              width="100%"
              alignSelf="center"
              _active={{ backgroundColor: 'gray.500' }}
              opacity={0.4}
              marginTop="-3"
              marginBottom="2"
              borderRadius="2px"
            />
          )}
          <PanelHeader hasNoMarginBottom={true}>
            <PanelTitle>{title}</PanelTitle>
            <OrderEntriesPanelToolbar
              isIsolationToggleActive={isIsolationToggleActive}
              setIsolationToggleActive={setIsolationToggleActive}
              projectId={projectId}
              selectedRootRows={selectedRootRows}
              selectedRowDbIds={selectedRowDbIds}
              propertySets={propertySets}
              isSubmittingEditForm={isSubmittingEditForm}
              isEditingOrAddingRow={tableMeta.isEditing}
              orderColumns={orderColumns ?? []}
              rowColors={rowColors}
              createOrderEntry={createOrderEntry}
              handleCreateFromTemplateClick={handleCreateFromTemplateClick}
              handleEditRowClick={handleEditRowClick}
              handleCancelEdit={handleCancelEdit}
              handleDeleteEntries={handleDeleteEntries}
              handleGroupBySubmit={handleGroupBySubmit}
              handleRemoveGroupBy={handleRemoveGroupBy}
              handleDynamicLinkingSubmit={handleDynamicLinkingSubmit}
              handleRemoveDynamicLinking={handleRemoveDynamicLinking}
              handleClearPromptLinking={handleClearPromptLinking}
              handleFlattenSubmit={handleFlattenSubmit}
              handleColumnManagementClick={saveColumns}
              handleExportToXlsx={exportAllRowsToExcel}
              handleAddItemClick={handleAddItemClick}
              handleMoveOrCopyItemsClick={handleMoveOrCopyItemsClick}
              handleDuplicateClick={handleDuplicateClick}
              handleCreateTemplateClick={handleCreateTemplateClick}
              handleResetColumnWidths={resetColumnWidths}
              linkElementsToRow={linkElementsToRow}
              unlinkElementsFromRow={unlinkElementsFromRow}
              unlinkAllElementsFromRow={unlinkAllElementsFromRow}
              isUpdatingTable={updating}
              hideElementsForRows={hideElementsForRows}
              deleteCustomColumn={deleteCustomColumn}
              readOnly={readOnly}
              projectSettings={cleanedProjectSettings}
              isHistoryMode={isHistoryDrawerOpen}
            />
          </PanelHeader>
          <PanelContent
            ref={panelRef}
            position="relative"
            display="flex"
            cursor={updating ? 'wait' : 'auto'}
            height={isFullscreen ? '100%' : panelHeight}
            minHeight={isFullscreen ? undefined : 100}
            maxHeight={isFullscreen ? undefined : '75vh'}
            flex={isFullscreen ? 1 : undefined}
            paddingBottom={0}
            paddingX={2}
          >
            <Fade in={updating} unmountOnExit>
              <Box
                position="absolute"
                top="40%"
                left="50%"
                textAlign="center"
                zIndex={2}
              >
                <Spinner color="brand.400" />
                <Text
                  size="sm"
                  color="gray.500"
                  _dark={{
                    color: 'gray.300',
                  }}
                >
                  {t('updating-table')}
                </Text>
              </Box>
            </Fade>
            <form
              style={{ display: 'none' }}
              id="edit-table-entry-form"
              onSubmit={onSubmitForm}
            />
            <FormProvider
              {...{
                register: registerToEditForm,
                reset: resetEditForm,
                handleSubmit: handleSubmitEditForm,
                setValue: setEditFormValue,
                getValues: getEditFormValues,
                watch: watchEditForm,
                formState: {
                  isSubmitting: isSubmittingEditForm,
                  errors: errorsEditForm,
                  ...restEditFormState,
                },
                control: editControl,
                ...restEditForm,
              }}
            >
              <SparkelTable
                tableInstance={table}
                newRowRef={newRowRef}
                newRowId={newRowId}
                onRowDoubleClick={(r) => handleEditRows([r])}
                onColumnRename={onColumnRename}
                onColumnDrop={onColumnDrop}
                onConfirmColumnDrop={handleConfirmColumnDrop}
                existingColumnNames={orderColumns.map((column) => column.name)}
                activeDropColumnData={activeDropColumnData}
                closeColumnDropConfirmation={clearActiveDropColumnData}
                readOnly={readOnly}
                updating={updating}
                showFooter={showFooter}
              >
                {resourceSuggestionsNode}
              </SparkelTable>
            </FormProvider>
          </PanelContent>
        </Panel>
      </GridItem>
      <Modal
        isOpen={buyModalIsOpen}
        onClose={closeBuyModal}
        isCentered
        size={'full'}
      >
        <ModalOverlay />
        <Buy
          title={t('paywall:item-limit-reached.title')}
          subtitle={t('paywall:item-limit-reached.subtitle')}
          onClose={closeBuyModal}
        />
      </Modal>
    </>
  );
}

// Effect for triggering isolation/colorization of linked elements for rows
function useRowHighlightingEffect(
  selectedRowDbIds: DbIdsPerModel,
  selectedFlatRows: Row<TableEntry>[],
  table: Table<TableEntry>
): [boolean, (value: boolean) => void] {
  const [isIsolationToggleActive, setIsolationToggleActive] =
    useLocalStorage<boolean>('isolate-linked-elements', true);

  const { isProtan } = useUserTenant();

  const { viewerMode, setViewerMode } = useViewerMode();

  const { isHistoryDrawerOpen, diff } = useProjectHistory();
  const rows = table.getRowModel().rows;

  const {
    loadedModels,
    isAllModelsLoaded,
    shapesManager: { loadedShapeModels },
  } = useViewer();

  const {
    shapesManager: { renderedSheetShapes: renderedSheetShapesV1 },
    activeSheetId,
    changeActiveSheet,
    pageNumber,
    setPageNumber,
  } = useSheetViewer();

  const {
    shapesManager: { renderedSheetShapes: renderedSheetShapesV2 },
  } = useSheetViewerV2();

  const enabledSheetViewer = useEnabledSheetViewer();

  const { colorSection, clearColors } = useColorization();
  const { isolate } = useVisibility();
  const { shapes: sheetShapes } = useSheetShapes();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();

  const renderedSheetShapes =
    sheetsViewerMode === ViewerMode.SheetsV2
      ? renderedSheetShapesV2
      : renderedSheetShapesV1;

  const previousRowIds = useRef<string[]>();
  const previousDbIds = useRef<string[]>();
  const previousToggleValue = useRef<boolean>();
  const previousColor = useRef<string[]>();
  const previousGroupBy = useRef<{ category: string; name: string }[][]>();
  const previousDiff = useRef<Record<string, any> | null>(null);
  const previousUrnSignatureRef = useRef<string | null>(null);

  const enableChangeViewerModeOnIsolation = useFlag(
    'enable-change-viewer-mode-on-isolation'
  );

  const areDbIdsInModelSpace = useCallback((dbIdsPerModel: DbIdsPerModel) => {
    let hasNonEmptyModelUrns = false;

    const result = Object.entries(dbIdsPerModel).every(([urn, dbIds]) => {
      // Skip URNs without dbIds or with empty dbIds
      if (!dbIds || dbIds.length === 0) {
        return true;
      }

      // This urn has dbIds, mark that we've found at least one non-empty model urn
      hasNonEmptyModelUrns = true;

      // Check if the urn is a sheet shape
      return !atob(urn).includes('sheet-shape');
    });

    // If no non-empty model URNs were found, return false
    return hasNonEmptyModelUrns && result;
  }, []);

  const areDbIdsOnSameSheetPage = useCallback(
    (dbIdsPerModel: DbIdsPerModel) => {
      let areAllSheetShapesOnSamePage = true;
      let hasValidSheetShapes = false;
      let commonSheetId: string | undefined = undefined;
      let commonPageNumber: number | undefined = undefined;

      // Iterate over each urn and its dbIds
      Object.entries(dbIdsPerModel).every(([urn, modelDbIds]) => {
        // Check if there are dbIds for the current urn
        if (!modelDbIds || modelDbIds.length === 0) {
          return true; // Skip this urn but continue processing others
        }

        if (sheetShapes && sheetShapes.data) {
          // Find the first sheet shape for the current urn
          const firstSheetShape = sheetShapes.data.find(
            (shape) =>
              getSheetShapeUrn(shape.projectId) === urn &&
              shape.dbId === modelDbIds[0]
          );

          // If the first sheet shape is not found, set the flag to false and stop further processing
          if (!firstSheetShape) {
            areAllSheetShapesOnSamePage = false;
            return false;
          }

          // Update common sheetId and pageNumber if they haven't been set yet
          if (!hasValidSheetShapes) {
            commonSheetId = firstSheetShape.sheetId;
            commonPageNumber = firstSheetShape.sheetPageNumber;
          }

          // Check if all dbIds for this urn are on the same sheet and page
          if (
            modelDbIds.every((dbId) => {
              const shape = sheetShapes.data?.find(
                (shape) =>
                  shape.dbId === dbId &&
                  getSheetShapeUrn(shape.projectId) === urn
              );

              return (
                shape &&
                shape.sheetId === commonSheetId &&
                shape.sheetPageNumber === commonPageNumber
              );
            })
          ) {
            hasValidSheetShapes = true;
            return true;
          } else {
            areAllSheetShapesOnSamePage = false;
            return false;
          }
        }
      });

      return {
        areAllSheetShapesOnSamePage:
          areAllSheetShapesOnSamePage && hasValidSheetShapes,
        commonSheetId,
        commonPageNumber,
      };
    },
    [sheetShapes]
  );

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (
      viewerMode === ViewerMode.Models &&
      (isEmpty(loadedModels) || !isAllModelsLoaded)
    ) {
      return;
    }

    // Exclude dbIds from unloaded models
    const targetDbIds = pick(selectedRowDbIds, [
      ...Object.keys(loadedShapeModels),
      ...Object.keys(loadedModels),
      ...Object.keys(renderedSheetShapes),
    ]);

    const rowNamesForIsolation = selectedFlatRows
      .map((row) => row.original.name)
      .sort();
    const selectedForDbIds = Object.entries(targetDbIds)
      .filter(([, dbIds]) => !isEmpty(dbIds))
      .map(([urn, dbIds]) => `${urn}:${dbIds.sort().join(',')}`)
      .sort();

    const selectedRowColors = selectedFlatRows
      .map((row) => row.original.color)
      .filter((color) => color !== undefined) as string[];

    const selectedGroupBy = selectedFlatRows
      .map((row) => row.original.groupByAttributes)
      .filter((groupBy) => groupBy !== undefined && groupBy !== null) as {
      category: string;
      name: string;
    }[][];

    // Trigger isolation/colorization iff
    // 1) The selected rows changes
    // 2) The dbIds for the selected rows change
    // 3) The isolation toggle changes state
    // 4) The color for the selected rows changes
    // 5) The grouping for the selected rows changes
    // 6) Tenant is not Protan
    const shouldTrigger =
      !isEqual(rowNamesForIsolation, previousRowIds.current) ||
      !isEqual(selectedForDbIds, previousDbIds.current) ||
      isIsolationToggleActive !== previousToggleValue.current ||
      !isEqual(selectedRowColors, previousColor.current) ||
      !isEqual(selectedGroupBy, previousGroupBy.current) ||
      !isEqual(diff, previousDiff.current);

    if (shouldTrigger && !isProtan) {
      // eslint-disable-next-line complexity
      const colorFunc = (urn: string, dbId: number) => {
        if (isHistoryDrawerOpen && diff) {
          // storing the colors for an element colorObj allows us to finish iterating the loop
          // finishing the loop allows us to find the correct color for the current urn,dbid pair
          const colorObj: Record<string, string> = {};
          const colorId = `${urn}-${dbId}`;
          const setColorForElement = (
            oldRawDbids: string | undefined,
            newRawDbids: string | undefined
          ) => {
            const oldDbids: Array<number> = JSON.parse(oldRawDbids ?? '[]');
            const newDbids: Array<number> = JSON.parse(newRawDbids ?? '[]');
            const inOld = oldDbids.find((oldDbid) => oldDbid === dbId);
            const inNew = newDbids.find((newDbid) => newDbid === dbId);
            if (inNew && !inOld) {
              // If in new and not in old then it has been added -> return the created color
              colorObj[colorId] = HISTORY_COLORS.CREATED;
            }
            if (!inNew && inOld) {
              // if in old and not in new, then it has been removed -> return the deleted color
              colorObj[colorId] = HISTORY_COLORS.DELETED;
            }
          };
          // shouldUseHistoricColor tells us if to gray out the models for contrast or skip and use the normal row colors
          let shouldUseHistoricColor = false;
          for (const row of selectedFlatRows) {
            const orderId = row.original.orderId;
            const entryId = row.original.id;
            let collectionPath = `project.orders.${orderId}.orderEntries.${entryId}`;
            const subPath = getEntryDiffPath(rows, row.id);
            // What about collections?
            collectionPath += subPath;
            const subCollectionPath = `${collectionPath}.resolvedElementIdentifiersDbid.${urn}`;
            const collectionDiff =
              diff[collectionPath] || diff[subCollectionPath];
            if (collectionDiff) {
              // Means that this is a new object so we have to key into it
              shouldUseHistoricColor = true;
              const oldRawDbids =
                collectionDiff.old?.resolvedElementIdentifiersDbid?.[urn]
                  ?.dbIds ?? collectionDiff.old?.dbIds;
              const newRawDbids =
                collectionDiff.new?.resolvedElementIdentifiersDbid?.[urn]
                  ?.dbIds ?? collectionDiff.new?.dbIds;
              setColorForElement(oldRawDbids, newRawDbids);
              continue;
            }
            const diffPath = `${collectionPath}.resolvedElementIdentifiersDbid.${urn}.dbIds`;
            const dbidDiff = diff[diffPath];
            if (dbidDiff) {
              shouldUseHistoricColor = true;
              setColorForElement(dbidDiff.old, dbidDiff.new);
            }
          }
          if (shouldUseHistoricColor) return colorObj[colorId] || '#dcdcdc';
        }

        // Filter out rows that use gradient and have subrows
        for (const row of selectedFlatRows.filter(
          (row) =>
            !(
              row.original.color?.includes('gradient') &&
              row.original.subRows &&
              row.original.subRows?.length > 0
            )
        )) {
          invariant(row.original.color, 'Color not found for row');
          if (!row.original.resolvedElementIdentifiersDbid) {
            continue;
          }
          if (
            row.original.resolvedElementIdentifiersDbid.some(
              (e) => e.modelUrn === urn && e.dbIds.includes(dbId)
            )
          ) {
            // Return the full color string (may include pattern encoding)
            // Downstream code will decode if needed (hexToRgb strips pattern for Forge,
            // decodeColorWithPattern extracts pattern for sheet shapes)
            return row.original.color;
          }
        }
        return '#000000';
      };
      if (isIsolationToggleActive) {
        // Use the unified visibility hook which now uses shared visibility managers
        isolate(targetDbIds);

        if (enableChangeViewerModeOnIsolation) {
          const nonEmptyEntries = Object.entries(targetDbIds).filter(
            ([, ids]) => ids && ids.length > 0
          );

          // Build a stable signature of current non-empty URNs to compare with previous
          const signature =
            nonEmptyEntries.length === 0
              ? 'EMPTY'
              : nonEmptyEntries
                  .slice()
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([urn, ids]) => `${urn}:${ids.length}`)
                  .join('|');

          if (previousUrnSignatureRef.current === signature) {
            // No change in URN set; do not switch viewer modes
          } else {
            previousUrnSignatureRef.current = signature;

            // If there are no new URNs, stay in the current mode
            if (nonEmptyEntries.length === 0) {
              // do nothing
            } else {
              const isSheetUrn = (urn: string) => {
                try {
                  return atob(urn).includes('sheet-shape');
                } catch {
                  return false;
                }
              };

              const allSheetShapes = nonEmptyEntries.every(([urn]) =>
                isSheetUrn(urn)
              );
              const allNonSheetShapes = nonEmptyEntries.every(
                ([urn]) => !isSheetUrn(urn)
              );

              if (allSheetShapes) {
                if (viewerMode !== sheetsViewerMode) {
                  setViewerMode(sheetsViewerMode);
                }

                const shapesData = sheetShapes.data || [];
                const uniqueSheetIds: string[] = [];
                const uniquePages: number[] = [];
                const pairs: Set<string> = new Set<string>();

                for (const [urn, ids] of nonEmptyEntries) {
                  for (const id of ids) {
                    const shape = shapesData.find(
                      (s) =>
                        getSheetShapeUrn(s.projectId) === urn && s.dbId === id
                    );
                    if (!shape) continue;
                    if (!uniqueSheetIds.includes(shape.sheetId))
                      uniqueSheetIds.push(shape.sheetId);
                    if (!uniquePages.includes(shape.sheetPageNumber))
                      uniquePages.push(shape.sheetPageNumber);
                    pairs.add(`${shape.sheetId}:${shape.sheetPageNumber}`);
                  }
                }

                if (uniqueSheetIds.length > 0) {
                  const ctx = enabledSheetViewer as
                    | SheetViewerContextType
                    | SheetsViewerV2ContextType;
                  const currentActiveSheetId = ctx.activeSheetId as
                    | string
                    | null;
                  const currentPageNumber =
                    'pagination' in ctx
                      ? ctx.pagination.pageNumber
                      : (ctx as SheetViewerContextType).pageNumber;
                  const isCurrentIncluded =
                    currentActiveSheetId !== null &&
                    pairs.has(`${currentActiveSheetId}:${currentPageNumber}`);

                  if (!isCurrentIncluded) {
                    if (
                      !currentActiveSheetId ||
                      !uniqueSheetIds.includes(currentActiveSheetId)
                    ) {
                      enabledSheetViewer.changeActiveSheet(uniqueSheetIds[0]);
                      if (
                        uniqueSheetIds.length === 1 &&
                        uniquePages.length > 0
                      ) {
                        enabledSheetViewer.setPageNumber(uniquePages[0]);
                      }
                    } else if (uniqueSheetIds.length === 1) {
                      if (
                        !uniquePages.includes(currentPageNumber) &&
                        uniquePages.length > 0
                      ) {
                        enabledSheetViewer.setPageNumber(uniquePages[0]);
                      }
                    }
                  }
                }
              } else if (allNonSheetShapes) {
                if (viewerMode !== ViewerMode.Models) {
                  setViewerMode(ViewerMode.Models);
                }
              }
            }
          }
        }
      }

      clearColors();

      colorSection(colorFunc, targetDbIds);

      previousRowIds.current = rowNamesForIsolation;
      previousDbIds.current = selectedForDbIds;
      previousToggleValue.current = isIsolationToggleActive;
      previousColor.current = selectedRowColors;
      previousGroupBy.current = selectedGroupBy;
      previousDiff.current = diff;
    }
  }, [
    activeSheetId,
    areDbIdsInModelSpace,
    areDbIdsOnSameSheetPage,
    changeActiveSheet,
    clearColors,
    colorSection,
    enableChangeViewerModeOnIsolation,
    isAllModelsLoaded,
    isIsolationToggleActive,
    isolate,
    loadedModels,
    loadedShapeModels,
    pageNumber,
    renderedSheetShapes,
    selectedFlatRows,
    selectedRowDbIds,
    setPageNumber,
    setViewerMode,
    viewerMode,
    sheetsViewerMode,
    rows,
    isHistoryDrawerOpen,
    diff,
  ]);

  return [isIsolationToggleActive, setIsolationToggleActive];
}

function columnToId(column: OrderColumnDeepFragment) {
  if (
    column.columnType === ColumnType.Custom ||
    column.columnType === ColumnType.ParentOrderLabel ||
    column.columnType === ColumnType.ItemGroup
  ) {
    return column.id;
  } else {
    return columnTypeToId(column.columnType);
  }
}

function columnTypeToId(
  columnType: Exclude<
    ColumnType,
    ColumnType.Custom | ColumnType.ParentOrderLabel | ColumnType.ItemGroup
  >
) {
  switch (columnType) {
    case ColumnType.Description:
      return 'name';
    case ColumnType.Unit:
      return 'unit';
    case ColumnType.Color:
      return 'color';
    case ColumnType.Quantity:
      return 'quantity';
    case ColumnType.UnitPrice:
      return 'unitPrice';
    case ColumnType.Total:
      return 'total';
    case ColumnType.LinkedCount:
      return 'linkedCount';
    case ColumnType.ParentOrder:
      return 'parentOrder';
    case ColumnType.ParentOrderStatus:
      return 'parentOrderStatus';
  }
}

// Get back the original column id from columnToId
function columnIdToOrderColumnId(
  columnId: string,
  columns: OrderColumnDeepFragment[]
): string {
  const column = columns.find((column) => columnToId(column) === columnId);
  invariant(column, `Column with id ${columnId} not found`);
  return column.id;
}

const toQuantityInput = (
  quantity: EditableRowFormValues['quantity'],
  columns: ColumnNameAndId[]
): QuantityInput | null => {
  if (!quantity) {
    return null;
  } else if (quantity.type === QuantityEnum.Dynamic) {
    return {
      type: QuantityEnum.Dynamic,
      calculation: quantity.calculation,
      unitFormatOverride: quantity.unitFormatOverride,
    };
  } else if (quantity.type === QuantityEnum.Derived) {
    return {
      type: QuantityEnum.Derived,
      derivedFormula: parseDerivedFormula(
        stringifyDerivedFormula(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          quantity.derivedFormula!,
          columns
        ),
        columns
      ),
    };
  } else if (quantity.type === QuantityEnum.DatabaseLookup) {
    return {
      type: QuantityEnum.DatabaseLookup,
      databaseLookup: quantity.databaseLookup,
    };
  } else {
    return {
      type: QuantityEnum.Static,
      value: quantity.value,
      stringValue: quantity.stringValue,
    };
  }
};

const toCustomFieldInput = (
  customFields: EditableRowFormValues['custom'],
  columns: ColumnNameAndId[]
): CustomFieldInput[] => {
  if (!customFields) {
    return [];
  }
  return Object.entries(customFields)
    .filter(([, field]) => field.quantity)
    .map(([orderColumnId, { id, quantity }]) => ({
      id,
      orderColumnId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      value: toQuantityInput(quantity!, columns)!,
    }));
};

const toUpdateEntryInput = (
  formValues: EditableRowFormValues,
  columns: ColumnNameAndId[],
  entryId: string
): OrderEntryOnOrderEntryForOrderEntriesOrderIdFkeyUsingOrderEntriesPkeyUpdate => {
  const { custom, quantity, ...rest } = formValues;
  const customFields = toCustomFieldInput(custom, columns);
  const quantityInput = toQuantityInput(quantity, columns);
  const [customFieldsToCreate, customFieldsToUpdate] = partition(
    customFields,
    (customField) => !customField.id
  );

  return {
    id: entryId,
    patch: {
      ...rest,
      // Invalidate resolved like this for now
      quantity: quantityInput,
      customFields: {
        create:
          customFieldsToCreate.length > 0
            ? customFieldsToCreate.map((fieldtoCreate) => ({
                orderColumn: {
                  connectById: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    id: fieldtoCreate.orderColumnId!,
                  },
                },
                value: fieldtoCreate.value,
              }))
            : undefined,
        updateByOrderColumnIdAndOrderEntryId:
          customFieldsToUpdate.length > 0
            ? customFieldsToUpdate.map((customField) => ({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                orderColumnId: customField.orderColumnId!,
                orderEntryId: entryId,
                patch: {
                  value: customField.value,
                },
              }))
            : undefined,
      },
    },
  };
};

function toQuantityFormData(
  quantity: QuantityType | null | undefined,
  resolvedQuantity: ResolvedQuantity | null | undefined,
  columns: OrderColumnDeepFragment[]
): QuantityType | undefined {
  if (isNil(quantity)) {
    return undefined;
  }
  if (quantity?.derivedFormula) {
    return {
      ...quantity,
      derivedFormula: stringifyDerivedFormula(quantity.derivedFormula, columns),
      stringValue: resolvedQuantity
        ? formatQuantity(
            resolvedQuantity,
            quantity.unitFormatOverride || undefined
          )
        : undefined,
    };
  } else {
    return {
      ...quantity,
      stringValue: resolvedQuantity
        ? formatQuantity(
            resolvedQuantity,
            quantity.unitFormatOverride || undefined
          )
        : undefined,
      unitFormatOverride: quantity.unitFormatOverride,
    };
  }
}

function toCustomFormData(
  customFields: ResolvedCustomField[] | undefined,
  columns: OrderColumnDeepFragment[]
): Record<string, { id: string; quantity?: QuantityTypeInput }> {
  return fromPairs(
    (customFields ?? []).map(({ orderColumnId, value, resolvedValue, id }) => [
      orderColumnId,
      {
        id,
        quantity: {
          ...value,
          stringValue: formatQuantity(
            resolvedValue,
            value.unitFormatOverride || undefined
          ),
          derivedFormula: value.derivedFormula
            ? stringifyDerivedFormula(value.derivedFormula, columns)
            : value.derivedFormula,
          unitFormatOverride: value.unitFormatOverride,
        },
      },
    ])
  );
}

export function dbIdsPerModelToInput(
  dbIds: DbIdsPerModel
): ElementIdentifierDbidTypeInput[] {
  return Object.entries(dbIds).map(([modelUrn, dbIds]) => ({
    modelUrn,
    dbIds,
  }));
}

function getQuantityForVisualizedRow(
  columnName: string,
  row: Row<TableEntry>,
  orderColumns: OrderColumnDeepFragment[]
) {
  if (
    columnName ===
    orderColumns.find((e) => e.columnType === ColumnType.Quantity)?.name
  ) {
    return row.original.quantity;
  } else {
    const columnId = orderColumns.find((e) => e.name === columnName)?.id;
    if (!columnId || !row.original.customFields) {
      return;
    }
    return row.original.customFields.find((e) => e.orderColumnId === columnId)
      ?.value;
  }
}
